// Operator dashboard.
// Grouped left-rail nav (INTAKE / RUNS / SIGNALS / SETTINGS): Applications +
// access-codes (mint/revoke/comp), a Live runs view (in-flight list + detail
// with an SSE stage rail, cost-so-far, and a "k of N converged" fan-out count),
// Run history, Metrics, Feedback, a How-heard attribution rollup, an Audit
// log of mutating actions, and the split Settings (Run mode & keys / Limits /
// Pricing) + Email (config / templates) leaves. Live runs / Feedback carry
// ●N count badges.
//
// Gating is layered: CF Access OTP at the edge (scoped to /operator*) + the
// server's hybrid gate (verified CF Access JWT OR X-Operator-Token). On the
// hosted surface OperatorShell auto-skips the token form when the JWT probe
// passes; localhost/dev falls back to the token form.
import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import {
  operatorApproveApplication,
  operatorDeclineApplication,
  operatorSetApplicationConcierge,
  operatorCancelRun,
  operatorGetRunReceipt,
  operatorGetMetrics,
  operatorListAccessCodes,
  operatorListApplications,
  operatorListAuditLog,
  operatorListFeedback,
  operatorListHowHeard,
  operatorAddHowHeard,
  operatorSetRatingReviewed,
  operatorListRuns,
  operatorListPeople,
  operatorPersonActivity,
  operatorDownloadRunBundle,
  operatorRunStatus,
  subscribeOperatorEvents,
  operatorMintAccessCodes,
  operatorResendAccessCodeEmail,
  operatorRevokeAccessCode,
  operatorSetAccessCodeComp,
  operatorUpdateFeedbackStatus,
  operatorGetSettings,
  operatorSetMode,
  operatorSetFlag,
  operatorSetEnum,
  operatorSetPilotKey,
  operatorClearPilotKey,
  operatorSetLimit,
  operatorGetPricing,
  operatorSetTierPrice,
  operatorSetEmailAddress,
  operatorSetEmailTemplate,
  operatorSendTestEmail,
  operatorListPortalClients,
  operatorOnboardPortalClient,
  operatorProvisionPortalClient,
  operatorArchivePortalClient,
  operatorRestorePortalClient,
  operatorResendPortalAgreement,
  operatorRotatePortalCode,
  operatorSendPortalInvite,
  type ApproveApplicationResponse,
  type OperatorAccessCode,
  type OperatorApplication,
  type OperatorAuditEntry,
  type OperatorFeedback,
  type OperatorFeedbackRating,
  type OperatorHowHeard,
  type OperatorRunRecord,
  type OperatorPerson,
  type OperatorPersonActivity,
  type StatusResponse,
  type SseEvent,
  type OperatorMetrics,
  type OperatorSettings,
  type OperatorFanoutMode,
  type OperatorLimitState,
  type OperatorPricing,
  type OperatorTierPrice,
  type OperatorEmailSettings,
  type OperatorEmailAddressName,
  type OperatorEmailTemplateName,
  type PortalClient,
} from "../lib/api";
import { STAGING_HOST } from "../lib/server-config";

type Tab = "people" | "applications" | "access-codes" | "clients" | "live-runs" | "runs" | "metrics" | "feedback" | "how-heard" | "audit" | "settings-runmode" | "settings-limits" | "settings-pricing" | "email-config" | "email-templates";

/**
 * Format a server-issued ISO-8601 timestamp for operator display.
 * Returns "—" for null / parse failure so the table never shows the
 * literal string "null" or an Invalid Date.
 */
function formatLocalTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

type Props = {
  server: string;
  token: string;
  // Optional: when omitted, JWT auth is providing the session and
  // sign-out should not show — CF Access owns the session lifetime.
  onSignOut?: () => void;
};

// Left-nav information architecture: the 9 panels grouped into four sections.
// Labels match the ops-panel redesign mockup; "Settings" stays one item because
// the SettingsPanel covers run mode / keys / limits / pricing together.
const NAV_GROUPS: { title: string; items: { value: Tab; label: string }[] }[] = [
  {
    title: "Intake",
    items: [
      { value: "people", label: "People" },
      { value: "applications", label: "Applications" },
      { value: "access-codes", label: "Access codes" },
    ],
  },
  {
    title: "Clients",
    items: [{ value: "clients", label: "Clients" }],
  },
  {
    title: "Runs",
    items: [
      { value: "live-runs", label: "Live runs" },
      { value: "runs", label: "Run history" },
      { value: "metrics", label: "Metrics" },
    ],
  },
  {
    title: "Signals",
    items: [
      { value: "feedback", label: "Feedback" },
      { value: "how-heard", label: "How heard" },
      { value: "audit", label: "Audit log" },
    ],
  },
  {
    title: "Settings",
    items: [
      { value: "settings-runmode", label: "Run mode & keys" },
      { value: "settings-limits", label: "Limits" },
      { value: "settings-pricing", label: "Pricing" },
      { value: "email-config", label: "Email" },
      { value: "email-templates", label: "Email templates" },
    ],
  },
];

// Nav count badges (●N): the at-a-glance "anything waiting on me" signal the
// flat top-nav couldn't carry. Each polls its own count, independent of which
// panel is open, and renders nothing at zero.
const NAV_BADGE_POLL_MS = 20_000;

function useCount(loader: () => Promise<number>): number | null {
  const [n, setN] = useState<number | null>(null);
  useEffect(() => {
    let alive = true;
    const run = () =>
      loader()
        .then((c) => {
          if (alive) setN(c);
        })
        .catch(() => {
          /* a failed background count just leaves the last value */
        });
    run();
    const t = setInterval(run, NAV_BADGE_POLL_MS);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [loader]);
  return n;
}

// Staging-only "you are spending real money" guard. On the staging host, poll
// settings and report TRUE whenever the operator has flipped the provider to
// real (Pilot/Live) — the box carries prod-seeded keys, so a forgotten flip
// spends real Anthropic budget / sends real mail until the next deploy resets
// it. Silent on prod (real is the normal state there) and when fake (the safe
// default). Keyed on use_fake_provider (the spend risk); banner copy also names
// email, which flips alongside it in live mode.
function useStagingLiveMode(server: string, token: string): boolean {
  const isStaging =
    typeof window !== "undefined" && window.location.hostname === STAGING_HOST;
  const [live, setLive] = useState(false);
  useEffect(() => {
    if (!isStaging) return;
    let alive = true;
    const run = () =>
      operatorGetSettings(server, token)
        .then((s) => {
          if (alive) setLive(s.use_fake_provider?.value === false);
        })
        .catch(() => {
          /* a failed background poll just leaves the last value */
        });
    run();
    const t = setInterval(run, NAV_BADGE_POLL_MS);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [isStaging, server, token]);
  return isStaging && live;
}

function NavBadge({ count }: { count: number | null }) {
  if (!count || count <= 0) return null;
  // aria-hidden: a decorative count chip. Keeping it out of the button's
  // accessible name leaves the nav label stable ("Feedback", not "Feedback 3")
  // — the panel itself surfaces the same count to assistive tech.
  return (
    <span className="operator-nav-badge" aria-hidden="true">
      {count}
    </span>
  );
}

// The owner of a run/code, resolved server-side (run -> access code ->
// application). Name is the primary line, email a muted secondary. A
// hand-minted code with no application shows the attached email and no name; a
// fully unlinked run shows the placeholder. Operator-only attribution — the
// same applicant email already appears on the Applications + Access-codes tabs.
//
// Callers pass `email={owner_email ?? notify_email}` (v16) so a self-serve
// run — no application behind its access code, the common case at launch —
// falls back to the address the user typed on the run screen instead of
// showing the placeholder for nearly the whole run population. This renders
// identically to a hand-minted code's attached email (email line, no name).
function OwnerCell({
  name,
  email,
  placeholder = "—",
}: {
  name?: string | null;
  email?: string | null;
  placeholder?: string;
}) {
  if (!name && !email) {
    return <span className="operator-owner-empty">{placeholder}</span>;
  }
  return (
    <span className="operator-owner">
      {name && <span className="operator-owner-name">{name}</span>}
      {email && <span className="operator-owner-email">{email}</span>}
    </span>
  );
}

// Shared cancel-run confirmation copy — used by Run history, the Live runs list,
// and the Live run detail. One source so the three call sites can't drift.
function cancelRunConfirm(target: string): string {
  return (
    `Cancel ${target}? The chain is killed, any in-flight batch is ` +
    `cancelled, the user's code is re-armed so they can run again.`
  );
}

export function OperatorDashboard({ server, token, onSignOut }: Props) {
  const [tab, setTab] = useState<Tab>("people");

  // Attention counts for the rail badges. In-flight runs + unreviewed feedback.
  const liveRunsCount = useCount(
    useCallback(
      () =>
        operatorListRuns(server, token).then(
          (r) => r.runs.filter((x) => IN_FLIGHT_STATUSES.has(x.status)).length,
        ),
      [server, token],
    ),
  );
  const feedbackCount = useCount(
    useCallback(
      () =>
        operatorListFeedback(server, token).then(
          (r) =>
            buildFeedbackEntries(r.feedback ?? [], r.ratings ?? []).filter(
              (e) => !e.reviewed,
            ).length,
        ),
      [server, token],
    ),
  );
  const stagingLive = useStagingLiveMode(server, token);

  return (
    <div className="operator-shell">
      <aside className="operator-rail">
        <div className="operator-rail-brand">
          Aurora<span>Operator</span>
        </div>
        <nav className="operator-rail-nav">
          {NAV_GROUPS.map((group) => (
            <div className="operator-nav-sec" key={group.title}>
              <div className="operator-nav-hd">{group.title}</div>
              {group.items.map((it) => (
                <button
                  key={it.value}
                  type="button"
                  className={`operator-nav-leaf${tab === it.value ? " active" : ""}`}
                  aria-pressed={tab === it.value}
                  onClick={() => setTab(it.value)}
                >
                  <span>{it.label}</span>
                  {it.value === "live-runs" && <NavBadge count={liveRunsCount} />}
                  {it.value === "feedback" && <NavBadge count={feedbackCount} />}
                </button>
              ))}
            </div>
          ))}
        </nav>
        {onSignOut && (
          <button
            type="button"
            className="operator-rail-signout"
            onClick={onSignOut}
          >
            ← Sign out
          </button>
        )}
      </aside>
      <main className="operator-main">
        {stagingLive && (
          <div className="operator-banner-live" role="alert">
            ⚠ STAGING IS LIVE — real Anthropic spend + real email on prod keys.
            Runs here cost real money. Flip Run mode back to Demo when you're done.
          </div>
        )}
        <div className="operator-banner">
          Operator surface — gated by Cloudflare Access OTP and operator token.
          Do not share this URL.
        </div>
        <section className="operator-tab-body">
          {tab === "people" && <PeoplePanel server={server} token={token} />}
          {tab === "applications" && (
            <ApplicationsPanel server={server} token={token} />
          )}
          {tab === "access-codes" && (
            <AccessCodesPanel server={server} token={token} />
          )}
          {tab === "clients" && <ClientsPanel server={server} token={token} />}
          {tab === "live-runs" && <LiveRunsPanel server={server} token={token} />}
          {tab === "runs" && <RunsPanel server={server} token={token} />}
          {tab === "metrics" && <MetricsPanel server={server} token={token} />}
          {tab === "feedback" && <FeedbackPanel server={server} token={token} />}
          {tab === "how-heard" && <HowHeardPanel server={server} token={token} />}
          {tab === "audit" && <AuditPanel server={server} token={token} />}
          {tab === "settings-runmode" && (
            <SettingsPanel server={server} token={token} section="runmode" />
          )}
          {tab === "settings-limits" && (
            <SettingsPanel server={server} token={token} section="limits" />
          )}
          {tab === "settings-pricing" && (
            <SettingsPanel server={server} token={token} section="pricing" />
          )}
          {tab === "email-config" && (
            <EmailPanel server={server} token={token} section="config" />
          )}
          {tab === "email-templates" && (
            <EmailPanel server={server} token={token} section="templates" />
          )}
        </section>
      </main>
    </div>
  );
}


type LoadState<T> =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "ready"; data: T }
  | { kind: "error"; message: string };


// Exported solely for direct testing — the race-clean generation-
// counter contract is load-bearing enough to deserve its own focused
// test that doesn't have to wrangle full-component renders.
export function useLoader<T>(loader: () => Promise<T>): {
  state: LoadState<T>;
  reload: () => void;
  reloadQuiet: () => void;
} {
  const [state, setState] = useState<LoadState<T>>({ kind: "idle" });
  // Generation counter — each reload bumps it; only the latest resolution
  // is allowed to update state. Replaces a per-call `cancelled` closure
  // that only worked for effect-driven calls; manual reload clicks now
  // race-cleanly too.
  const generationRef = useRef(0);
  const doLoad = useCallback((quiet: boolean) => {
    const myGen = ++generationRef.current;
    // Quiet refresh (background polling) keeps the last data on screen
    // instead of flashing the table back to "Loading…" every interval.
    if (!quiet) setState({ kind: "loading" });
    loader()
      .then((data) => {
        if (myGen !== generationRef.current) return;
        setState({ kind: "ready", data });
      })
      .catch((e: unknown) => {
        if (myGen !== generationRef.current) return;
        const message = e instanceof Error ? e.message : String(e);
        // A failed BACKGROUND poll keeps the stale-but-real data rather
        // than replacing the table with an error banner mid-watch.
        if (!quiet) setState({ kind: "error", message });
      });
  }, [loader]);
  const reload = useCallback(() => doLoad(false), [doLoad]);
  const reloadQuiet = useCallback(() => doLoad(true), [doLoad]);
  useEffect(() => {
    reload();
  }, [reload]);
  return { state, reload, reloadQuiet };
}


type ApproveBanner = {
  code: string;
  email: string;
  emailOk: boolean;
  emailError: string | null;
  faked: boolean; // ok=true but nothing actually sent (fake-email / no Resend key)
  noop: boolean; // idempotent replay — nothing minted or sent
};

// Human-readable labels for operator-table display. Keys match the
// server-side enum values in apply_routes.py. Centralized here so the
// dashboard reads uniformly across the table + the detail panel.
const ROLE_LABEL: Record<string, string> = {
  pro_se: "Pro se",
  attorney: "Attorney",
  advocate: "Advocate",
  court_appointed: "Court-appointed",
  researcher: "Researcher",
  other: "Other",
  // Legacy / pre-v5 rows may still carry these — pilot's earlier
  // applications used `gal` / `evaluator` / `expert` separately
  // before the v2 schema merged them into court_appointed.
  gal: "GAL",
  evaluator: "Evaluator",
  expert: "Expert",
};

const CASE_STATUS_LABEL: Record<string, string> = {
  preparing: "Preparing",
  filed: "Filed",
  hearing_scheduled: "Hearing scheduled",
  mid_proceeding: "Mid-proceeding",
  post_judgment: "Post-judgment",
  not_yet: "Not in court yet",
  other: "Other",
};

const HEARING_WINDOW_LABEL: Record<string, string> = {
  lt_2w: "<2 weeks",
  "2_6w": "2–6 weeks",
  "1_3m": "1–3 months",
  gt_3m: ">3 months",
  none: "No date",
};

const TIME_SPAN_LABEL: Record<string, string> = {
  lt_6m: "<6 months",
  "6m_2y": "6 mo – 2 yr",
  "2_5y": "2–5 years",
  gt_5y: ">5 years",
  unsure: "Unsure",
};

const SOURCE_LABEL: Record<string, string> = {
  ofw: "OFW",
  civil_communicator: "Civil Comm.",
  two_houses: "2houses",
  talking_parents: "TalkingParents",
  imessage: "iMessage",
  other: "Other",
  unsure: "Unsure",
};

const SUPPORT_STATUS_LABEL: Record<string, string> = {
  yes: "Working with an attorney/advocate",
  alone: "On their own",
  partial: "Some support, mostly on their own",
};

const ACTIVE_CASE_LABEL: Record<string, string> = {
  yes: "Yes — case in front of them",
  soon: "Soon — taking one on",
  not_yet: "Not yet — exploring",
};

// Urgency chip class. Under 2 weeks = oxblood, 2–6 = neutral, longer
// = muted. Mirrors the design-doc §5 spec.
function timingChipClass(hw: string | null | undefined): string {
  if (hw === "lt_2w") return "operator-chip operator-chip-urgent";
  if (hw === "2_6w") return "operator-chip operator-chip-soon";
  if (hw && hw !== "none") return "operator-chip operator-chip-far";
  return "operator-chip operator-chip-none";
}

// Collapse the sources list for the row column. One source → that
// label; two → "A + B"; three or more → "Multi (N)". Detail panel
// renders the full verbose list.
function summarizeSources(sources: string[] | undefined): string {
  if (!sources || sources.length === 0) return "—";
  if (sources.length === 1) return SOURCE_LABEL[sources[0]] ?? sources[0];
  if (sources.length === 2) {
    return sources
      .map((s) => SOURCE_LABEL[s] ?? s)
      .join(" + ");
  }
  return `Multi (${sources.length})`;
}

const APPLICATIONS_COL_COUNT = 9;


type AppStatusFilter =
  | "all"
  | "pending"
  | "approved"
  | "declined"
  | "concierge";

// --- People panel: the person-centric daily-driver view (Slice 1) ---
// One row per human, grouped server-side by owner email across applications +
// access codes + runs. Applications + Access codes stay their own leaves; this
// sits on top so "who is using the program / whose run is this" is one glance.

// "New since you last looked" — a per-browser last-seen marker so the operator
// can open the console and immediately see who/what is new (the in-console half
// of the run-activity awareness work; the Discord ping is the live half).
const PEOPLE_SEEN_KEY = "aurora.operator.peopleSeenAt";

const STAGE_LABEL: Record<string, string> = {
  ran: "Ran",
  code_issued: "Code issued",
  direct_code: "Direct code",
  applied: "Applied",
  set_aside: "Set aside",
  declined: "Declined",
  approved: "Approved",
};

function StageChip({ stage }: { stage: string }) {
  return (
    <span className={`operator-stage operator-stage--${stage}`}>
      {STAGE_LABEL[stage] ?? stage}
    </span>
  );
}

// Category → filter-chip label + a short glyph. Kept in sync with the
// server-side categories in activity.py.
const ACTIVITY_CATEGORIES: { key: string; label: string; glyph: string }[] = [
  { key: "code", label: "Code", glyph: "◆" },
  { key: "run", label: "Runs", glyph: "▸" },
  { key: "payment", label: "Payments", glyph: "$" },
  { key: "email", label: "Emails", glyph: "✉" },
  { key: "download", label: "Downloads", glyph: "↓" },
];

function ActivityTimeline({
  person,
  server,
  token,
}: {
  person: OperatorPerson;
  server: string;
  token: string;
}) {
  // Identify by an opaque handle — application id when present, else the
  // primary (newest) code's id. Never the email or code string.
  const applicationId = person.application_id;
  const codeId = person.codes[0]?.id ?? null;
  const loader = useCallback(
    () =>
      operatorPersonActivity(server, token, {
        applicationId,
        codeId,
      }),
    [server, token, applicationId, codeId],
  );
  const { state } = useLoader<OperatorPersonActivity>(loader);
  const [filter, setFilter] = useState<string>("all");
  const [bundleError, setBundleError] = useState<string | null>(null);

  if (state.kind === "loading" || state.kind === "idle") {
    return <p className="muted">Loading activity…</p>;
  }
  if (state.kind === "error") {
    return <p className="operator-activity-error">Couldn't load activity: {state.message}</p>;
  }
  const events = state.data.events;
  if (events.length === 0) {
    return <p className="muted">No recorded activity.</p>;
  }
  const counts = events.reduce<Record<string, number>>((acc, e) => {
    acc[e.category] = (acc[e.category] ?? 0) + 1;
    return acc;
  }, {});
  const shown =
    filter === "all" ? events : events.filter((e) => e.category === filter);

  return (
    <div className="operator-activity">
      <div className="operator-activity-filters" role="tablist" aria-label="Filter activity">
        <button
          type="button"
          className={`operator-activity-chip${filter === "all" ? " is-active" : ""}`}
          onClick={() => setFilter("all")}
          aria-pressed={filter === "all"}
        >
          All <span className="operator-activity-chip-n">{events.length}</span>
        </button>
        {ACTIVITY_CATEGORIES.filter((c) => counts[c.key]).map((c) => (
          <button
            key={c.key}
            type="button"
            className={`operator-activity-chip operator-activity-chip--${c.key}${
              filter === c.key ? " is-active" : ""
            }`}
            onClick={() => setFilter(c.key)}
            aria-pressed={filter === c.key}
          >
            <span className="operator-activity-glyph" aria-hidden="true">{c.glyph}</span>
            {c.label} <span className="operator-activity-chip-n">{counts[c.key]}</span>
          </button>
        ))}
      </div>
      <ol className="operator-activity-list">
        {shown.map((e, i) => {
          const bundleUrl =
            typeof e.detail?.bundle_url === "string" ? e.detail.bundle_url : null;
          const runId =
            typeof e.detail?.cloud_run_id === "string" ? e.detail.cloud_run_id : "";
          return (
            <li
              key={`${e.ts}-${e.type}-${i}`}
              className={`operator-activity-item operator-activity-item--${e.category}`}
            >
              <span className="operator-activity-marker" aria-hidden="true" />
              <div className="operator-activity-body">
                <span className="operator-activity-summary">
                  {e.summary}
                  {bundleUrl && (
                    <button
                      type="button"
                      className="operator-activity-link"
                      onClick={() => {
                        operatorDownloadRunBundle(server, token, bundleUrl, runId).catch(
                          (err: unknown) =>
                            setBundleError(
                              err instanceof Error ? err.message : String(err),
                            ),
                        );
                      }}
                      title="Download the encrypted result bundle (opaque without the user's passphrase)"
                    >
                      ↓ encrypted copy
                    </button>
                  )}
                </span>
                <span className="operator-activity-meta">
                  {formatLocalTime(e.ts)}
                  {e.actor ? ` · ${e.actor}` : ""}
                </span>
              </div>
            </li>
          );
        })}
      </ol>
      {bundleError && (
        <p className="operator-activity-error">
          Couldn't fetch the encrypted copy: {bundleError} (it may have expired).
        </p>
      )}
    </div>
  );
}

function PersonDetail({
  person,
  server,
  token,
}: {
  person: OperatorPerson;
  server: string;
  token: string;
}) {
  return (
    <div className="operator-person-detail">
      <div className="operator-person-cols">
        <section>
          <h4>Codes ({person.code_count})</h4>
          {person.codes.length === 0 ? (
            <p className="muted">None.</p>
          ) : (
            <ul className="operator-person-list">
              {person.codes.map((c) => (
                <li key={c.code}>
                  <code>{c.code}</code> · {c.status} · {c.comp ? "comp" : "paid"}
                  {c.created_at ? ` · ${formatLocalTime(c.created_at)}` : ""}
                </li>
              ))}
            </ul>
          )}
        </section>
        <section>
          <h4>Runs ({person.run_count})</h4>
          {person.runs.length === 0 ? (
            <p className="muted">None.</p>
          ) : (
            <ul className="operator-person-list">
              {person.runs.map((r) => (
                <li key={r.cloud_run_id}>
                  <code>{r.cloud_run_id}</code> · {r.status}
                  {r.cost_usd_anthropic != null
                    ? ` · $${r.cost_usd_anthropic.toFixed(2)}`
                    : ""}
                  {r.started_at ? ` · ${formatLocalTime(r.started_at)}` : ""}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
      {person.application_id && (
        <p className="muted operator-person-applink">
          Full application detail is on the Applications tab.
        </p>
      )}
      <section className="operator-person-activity-section">
        <h4>Activity</h4>
        <ActivityTimeline person={person} server={server} token={token} />
      </section>
    </div>
  );
}

function PersonRow({
  person,
  server,
  token,
  expanded,
  isNew,
  onToggle,
}: {
  person: OperatorPerson;
  server: string;
  token: string;
  expanded: boolean;
  isNew: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      <tr
        className={`operator-row${expanded ? " is-expanded" : ""}`}
        onClick={onToggle}
        aria-expanded={expanded}
      >
        <td>
          <OwnerCell name={person.name} email={person.email} />
          {isNew && <span className="operator-person-new">New</span>}
        </td>
        <td>{person.role ? ROLE_LABEL[person.role] ?? person.role : "—"}</td>
        <td><StageChip stage={person.stage} /></td>
        <td>
          {person.code ? <code>{person.code}</code> : "—"}
          {person.code_count > 1 && (
            <span className="muted"> +{person.code_count - 1}</span>
          )}
        </td>
        <td>{person.run_count}</td>
        <td>{person.last_activity ? formatLocalTime(person.last_activity) : "—"}</td>
      </tr>
      {expanded && (
        <tr className="operator-row-detail">
          <td colSpan={6}>
            <PersonDetail person={person} server={server} token={token} />
          </td>
        </tr>
      )}
    </>
  );
}

function PeoplePanel({ server, token }: { server: string; token: string }) {
  const loader = useCallback(
    () => operatorListPeople(server, token).then((r) => r.people),
    [server, token],
  );
  const { state, reload } = useLoader<OperatorPerson[]>(loader);
  const [query, setQuery] = useState("");
  const [stage, setStage] = useState<string>("all");
  const [expanded, setExpanded] = useState<string | null>(null);
  const [seenAt, setSeenAt] = useState<string>(() => {
    try {
      return localStorage.getItem(PEOPLE_SEEN_KEY) ?? "";
    } catch {
      return "";
    }
  });
  const markSeen = () => {
    const now = new Date().toISOString();
    try {
      localStorage.setItem(PEOPLE_SEEN_KEY, now);
    } catch {
      /* private-mode storage denied — the marker is best-effort */
    }
    setSeenAt(now);
  };

  return (
    <PanelChrome<OperatorPerson>
      title="People"
      subtitle="Everyone who has applied, been issued a code, or run an analysis — one row per person, newest activity first."
      state={state}
      onReload={reload}
      empty="No people yet."
      renderTable={(rows) => {
        const q = query.trim().toLowerCase();
        const filtered = rows.filter((p) => {
          if (stage !== "all" && p.stage !== stage) return false;
          if (!q) return true;
          return (
            (p.name ?? "").toLowerCase().includes(q) ||
            p.email.toLowerCase().includes(q) ||
            (p.code ?? "").toLowerCase().includes(q)
          );
        });
        // Robust epoch compare — server timestamps carry a +00:00 offset, the
        // localStorage marker an ISO "Z"; lexical comparison of the two is not safe.
        const seenMs = seenAt ? Date.parse(seenAt) : 0;
        const isNew = (p: OperatorPerson) =>
          !!p.last_activity && Date.parse(p.last_activity) > seenMs;
        const newCount = rows.filter(isNew).length;
        return (
          <>
            {newCount > 0 && (
              <div className="operator-newsince">
                <span>
                  <b>{newCount}</b> new since you last looked
                </span>
                <button
                  type="button"
                  className="operator-row-action"
                  onClick={markSeen}
                >
                  Mark seen
                </button>
              </div>
            )}
            <div className="operator-filters">
              <label>
                Stage{" "}
                <select value={stage} onChange={(e) => setStage(e.target.value)}>
                  <option value="all">All</option>
                  {Object.entries(STAGE_LABEL).map(([v, l]) => (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  ))}
                </select>
              </label>
              <input
                type="search"
                placeholder="name, email, or code"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            {filtered.length === 0 ? (
              <div className="operator-empty">No people match.</div>
            ) : (
              <table className="operator-table operator-table-people">
                <thead>
                  <tr>
                    <th>Person</th>
                    <th>Role</th>
                    <th>Stage</th>
                    <th>Code</th>
                    <th>Runs</th>
                    <th>Last activity</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p) => (
                    <PersonRow
                      key={p.email}
                      person={p}
                      server={server}
                      token={token}
                      expanded={expanded === p.email}
                      isNew={isNew(p)}
                      onToggle={() =>
                        setExpanded(expanded === p.email ? null : p.email)
                      }
                    />
                  ))}
                </tbody>
              </table>
            )}
          </>
        );
      }}
    />
  );
}

// --- Clients panel (done-for-you SFTPGo portal roster) ---
//
// Operator-side control plane for the portal client console: onboard a new
// client (provisions the SFTPGo cryptfs account), send the invite (shows the
// one-time temp password exactly once), and mark a packet delivered. The
// portal itself — upload/download — is SFTPGo's built-in web client; this
// panel never touches file content, only the roster metadata.

// Operator-only deep-link into a client's folder via the portal-ops SFTPGo web
// session. Base comes from VITE_PORTAL_OPS_WEB_BASE — a TAILNET address (binding
// 0, :3334), deliberately NOT the public portal, so operator file management
// stays off the public binding. Unset → the link is hidden (keeps the internal
// host out of the public bundle by default).
const OPS_WEB_BASE = (import.meta.env.VITE_PORTAL_OPS_WEB_BASE ?? "").trim();
export function opsFolderUrl(base: string, username: string): string {
  return `${base.replace(/\/+$/, "")}/web/client/files?path=${encodeURIComponent("/clients/" + username)}`;
}

// Humanize the control-plane errors so the operator never sees raw JSON.
function portalError(e: unknown, fallback: string): string {
  const msg = e instanceof Error ? e.message : String(e);
  if (/already exists/i.test(msg))
    return "That username is already taken — choose a different one.";
  if (/already_signed/i.test(msg))
    return "This client has already signed — there’s nothing to resend.";
  if (/account_missing/i.test(msg))
    return "This client’s file-server account no longer exists — archive it and re-onboard.";
  if (/unprovisioned/i.test(msg))
    return "This client doesn’t have an account yet.";
  if (/no email on file/i.test(msg))
    return "No email on file — use New code to issue a sign-up code you relay yourself, or Copy link to relay the agreement.";
  // Anything unmatched: a plain fallback, never the raw control-plane message (which can
  // be technical / leak internal detail). The specific cases above carry the next action.
  return fallback;
}

// Compact roster pill: only a `provisioned` client can actually sign in ("Active").
// `archived` = set aside; everything else (locked / legacy signed|invited) can't log
// in yet → "Locked". The rail's access timeline carries the finer detail.
export function portalPill(c: PortalClient): { cls: "active" | "locked" | "archived"; text: string } {
  if (c.status === "archived") return { cls: "archived", text: "Archived" };
  if (c.status === "provisioned") return { cls: "active", text: "Active" };
  return { cls: "locked", text: "Locked" };
}

// Where a provisioned client is in getting into their account. "warn" is the
// state Rich flagged: they've signed in on the temp but haven't set their own
// password yet — so the temp is still live and losing it strands them.
export function portalAccessProgress(
  c: PortalClient,
): { text: string; tone: "pending" | "warn" | "ok" } | null {
  if (c.status !== "provisioned") return null;
  if (c.password_set) return { text: "Password set ✓", tone: "ok" };
  if (c.last_login) return { text: "Signed in · password not set", tone: "warn" };
  return { text: "Awaiting first sign-in", tone: "pending" };
}

// A dated access timeline for the detail rail — onboarded → agreement → signed →
// first login → password. Built entirely from fields already on the roster (no
// new backend). "warn" on login/password is the state Rich flagged: signed in on
// the temp but no password yet, so the temp is still live.
export type AccessStep = { key: string; label: string; state: "done" | "warn" | "pending"; when: string | null };
function fmtDate(v: string | number | null): string | null {
  if (v == null || v === "") return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return typeof v === "string" ? v : null;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
export function portalAccessTimeline(c: PortalClient): AccessStep[] {
  const signed = c.status === "provisioned" || c.status === "signed";
  const tempLive = !!c.last_login && !c.password_set;
  return [
    { key: "onboarded", label: "Onboarded", state: "done", when: fmtDate(c.provisioned_at) },
    { key: "agreement", label: "Agreement sent",
      state: c.invite_sent_at || c.agreement_url || signed ? "done" : "pending", when: fmtDate(c.invite_sent_at) },
    { key: "signed", label: "Signed", state: signed ? "done" : "pending", when: null },
    { key: "login", label: "First login",
      state: c.last_login ? (tempLive ? "warn" : "done") : "pending", when: fmtDate(c.last_login) },
    { key: "password", label: "Password set",
      state: c.password_set ? "done" : tempLive ? "warn" : "pending", when: null },
  ];
}

// Which actions belong in each rail group, per client state — the SAME actions the
// flat row carried, regrouped by intent (primary / recovery / lifecycle). The panel
// maps each key to its existing handler + label + disabled state (renderAction).
export type ActionKey =
  | "invite" | "copyUsername" | "openFolder" | "resendAgreement"
  | "copyLink" | "newCode" | "enable" | "restore" | "archive";
export type ActionGroups = { primary: ActionKey | null; recovery: ActionKey[]; lifecycle: ActionKey[] };
export function portalActionGroups(c: PortalClient): ActionGroups {
  switch (c.status) {
    case "provisioned": {
      // Open folder leads when the operator has a tailnet ops session; otherwise the
      // most likely next touch is (re)sending the invite. Don't list the primary twice.
      // (Copy-username lives in the rail header, not as a chip.)
      const primary: ActionKey = OPS_WEB_BASE ? "openFolder" : "invite";
      const recovery = (["invite"] as ActionKey[]).filter((k) => k !== primary);
      return { primary, recovery, lifecycle: ["archive"] };
    }
    case "locked":
      return { primary: "resendAgreement", recovery: ["copyLink", "newCode"], lifecycle: ["enable", "archive"] };
    case "archived":
      return { primary: "restore", recovery: [], lifecycle: [] };
    case "signed":
      // Signed but not open yet. On the signing-sets-password flow this is a
      // client who signed and stopped before choosing a password — they finish
      // through the SAME link (code → password), so relaying it is the primary
      // move and a fresh code the recovery. Enable stays as the manual
      // override (note: on the new flow it opens a login that still has no
      // usable password — pair it with New code).
      return { primary: "copyLink", recovery: ["newCode"], lifecycle: ["enable", "archive"] };
    default: // legacy "invited": just relay the agreement link
      return { primary: "copyLink", recovery: [], lifecycle: ["archive"] };
  }
}

export function ClientsPanel({ server, token }: { server: string; token: string }) {
  const loader = useCallback(
    () => operatorListPortalClients(server, token).then((r) => r.clients), [server, token]);
  const { state, reload } = useLoader<PortalClient[]>(loader);
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<string>("");
  const [copied, setCopied] = useState<string | null>(null);
  const [copiedAgr, setCopiedAgr] = useState<string | null>(null);
  // The folder deep-link is copied (not opened): SFTPGo's SameSite=Strict cookie
  // won't ride a cross-site open from this origin, so pasting it into a logged-in
  // SFTPGo tab (a same-site address-bar nav) is the path that actually works.
  const [copiedFolder, setCopiedFolder] = useState<string | null>(null);
  // The one-time password from the last invite/reset, anchored to ITS client row
  // (not a floating note). Shown once — SFTPGo keeps no plaintext, so the only
  // "retrieval" is a fresh Resend, which the row makes explicit.
  // The one-time code detail, anchored to ITS client's roster row (not a floating
  // panel by the form) so the code is connected to WHO it's for and retrievable
  // until dismissed. `onboard` carries the agreement link + how the auto-email went,
  // shown only for a just-onboarded client; invite/New-code omit it.
  const [cred, setCred] = useState<{
    // emailOk: true = invite email sent, false = it failed, null = no email attempted
    // (the New-code / relay-it-yourself path — don't warn about an email we never tried).
    username: string; temp: string; emailOk: boolean | null;
    onboard?: { link: string | null; email: "sent" | "failed" | "none" | "manual" };
  } | null>(null);
  const [copiedTemp, setCopiedTemp] = useState(false);
  const [copiedCredLink, setCopiedCredLink] = useState(false);
  // The roster's stored one-time code (server keeps it until used — see
  // PortalClient.temp_code) renders masked; reveal is per-username so switching
  // clients re-masks, and a copy never requires revealing first.
  const [revealedCodeFor, setRevealedCodeFor] = useState<string | null>(null);
  const [copiedStoredCode, setCopiedStoredCode] = useState<string | null>(null);
  const [confirmReset, setConfirmReset] = useState<PortalClient | null>(null);
  // Enabling an UNSIGNED (locked) client bypasses their consent — the operator's
  // deliberate override, so it takes a confirm. Archive is reversible but still
  // confirmed so a stray click doesn't disable a live client mid-case.
  const [confirmEnable, setConfirmEnable] = useState<PortalClient | null>(null);
  const [confirmArchive, setConfirmArchive] = useState<PortalClient | null>(null);
  // Which client's detail rail is open. Falls back to the first row when null or
  // stale (see the rail's `sel`), so no load-time effect is needed.
  const [selected, setSelected] = useState<string | null>(null);
  // Guard against clobbering a shown-once code: while any action is in flight (busy) OR
  // an issued code is still displayed (cred), the credential-issuing controls (Send/
  // Resend invite, New code, and the onboard submit) are disabled across ALL rows — so a
  // second request can't replace the first client's not-yet-copied code before it's read.
  const credBusy = busy !== null || cred !== null;

  async function doInvite(c: PortalClient) {
    // Don't clobber an un-copied code: if one is showing, make the operator copy +
    // dismiss it first (the buttons are also disabled while cred is set — belt + braces).
    if (cred) { setNote("Copy the current one-time code and dismiss it before issuing another."); return; }
    setBusy(c.username); setNote(""); setConfirmReset(null); setCopiedTemp(false);
    try {
      const r = await operatorSendPortalInvite(server, token, c.username);
      // Same ② link block as onboard/New-code — the emailed button opens this
      // link, and if the send failed the operator relays it by hand.
      setCred({ username: c.username, temp: r.temp_password, emailOk: !!r.email_status?.ok,
                onboard: r.agreement_url
                  ? { link: r.agreement_url, email: r.email_status?.ok ? "sent" : "failed" }
                  : undefined });
      reload();
    } catch (e) { setNote(portalError(e, "Couldn’t send the invite.")); }
    finally { setBusy(null); }
  }

  // Confirm whenever a live credential would be clobbered: a previously sent
  // code (resend), or the password the client CHOSE for themselves — on the
  // signing flow that password exists even though no invite was ever sent, so
  // keying this on invite_sent_at alone silently broke a finished client's
  // login (review finding, 2026-07-14). Truly-fresh invites go straight through.
  function onInvite(c: PortalClient) {
    if (c.invite_sent_at || c.password_set) setConfirmReset(c);
    else void doInvite(c);
  }

  // Manual ENABLE — the backstop when signing didn't unlock the account (enable-on-
  // ack failed, or the link lapsed and consent was verified out-of-band).
  async function onEnable(c: PortalClient) {
    setBusy(c.username); setNote(""); setCred(null); setConfirmEnable(null);
    try {
      await operatorProvisionPortalClient(server, token, c.username);
      setNote(`Enabled ${c.username}. If they never chose a password, issue a New code so they can get in.`);
      reload();
    } catch (e) { setNote(portalError(e, "Couldn’t enable the client.")); }
    finally { setBusy(null); }
  }

  // Enabling a locked client who never signed needs confirmation; a signed-but-
  // stuck client (rare) is a clean recovery — enable straight through.
  function onEnableClick(c: PortalClient) {
    if (c.status === "locked") setConfirmEnable(c);
    else void onEnable(c);
  }

  // Resend the agreement link to a locked client: refreshes the 90-day window and
  // re-emails the link (needs an email on file).
  async function onResendAgreement(c: PortalClient) {
    setBusy(c.username); setNote(""); setCred(null);
    try {
      const r = await operatorResendPortalAgreement(server, token, c.username);
      setNote(r.email_status?.ok
        ? `Agreement link re-sent to ${c.username}.`
        : `Refreshed ${c.username}’s agreement link, but the email didn’t send — use Copy link to relay it.`);
      reload();
    } catch (e) { setNote(portalError(e, "Couldn’t resend the agreement.")); }
    finally { setBusy(null); }
  }

  // Rotate a fresh one-time login code (the onboard code is shown once; if it's
  // lost, this issues a new one and invalidates the old). Works on locked clients
  // too — the account exists, it's just disabled until they sign.
  async function onNewCode(c: PortalClient) {
    if (cred) { setNote("Copy the current one-time code and dismiss it before issuing another."); return; }
    setBusy(c.username); setNote(""); setCopiedTemp(false);
    try {
      // Rotate a code WITHOUT emailing (relay-it-yourself) — works even with no email on
      // file. /invite (Send invite) is the email path; this one never needs an address.
      const r = await operatorRotatePortalCode(server, token, c.username);
      // The code only works at the agreement link now (never the portal
      // login), so the card must hand the operator BOTH things to relay.
      setCred({ username: c.username, temp: r.temp_password, emailOk: null,
                onboard: { link: r.agreement_url ?? null, email: "manual" } });
      reload();
    } catch (e) { setNote(portalError(e, "Couldn’t issue a new code.")); }
    finally { setBusy(null); }
  }

  async function onArchive(c: PortalClient) {
    setBusy(c.username); setNote(""); setCred(null); setConfirmArchive(null);
    try {
      await operatorArchivePortalClient(server, token, c.username);
      setNote(`Archived ${c.username}. Their data is kept; Restore brings them back.`);
      reload();
    } catch (e) { setNote(portalError(e, "Couldn’t archive the client.")); }
    finally { setBusy(null); }
  }

  async function onRestore(c: PortalClient) {
    setBusy(c.username); setNote(""); setCred(null);
    try {
      const r = await operatorRestorePortalClient(server, token, c.username);
      setNote(r.status === "locked"
        ? `Restored ${c.username} — still awaiting their signature (login stays locked until they sign).`
        : `Restored ${c.username}. Their login is active again.`);
      reload();
    } catch (e) { setNote(portalError(e, "Couldn’t restore the client.")); }
    finally { setBusy(null); }
  }

  async function onCopyUsername(c: PortalClient) {
    try {
      await navigator.clipboard?.writeText(c.username);
      setCopied(c.username);
      window.setTimeout(() => setCopied((v) => (v === c.username ? null : v)), 2000);
    } catch { setNote(`Couldn’t copy — the username is ${c.username}`); }
  }

  // Resend the agreement link to an unsigned client (agreement-first, #30): the
  // roster carries it so a lost link is never a dead-end.
  async function onCopyAgreement(c: PortalClient) {
    if (!c.agreement_url) return;
    try {
      await navigator.clipboard?.writeText(c.agreement_url);
      setCopiedAgr(c.username);
      window.setTimeout(() => setCopiedAgr((v) => (v === c.username ? null : v)), 2000);
    } catch { setNote(`Couldn’t copy — the agreement link is ${c.agreement_url}`); }
  }

  // Copy the client's SFTPGo folder deep-link to paste into a logged-in SFTPGo tab
  // (see copiedFolder — a direct cross-site open can't carry the Strict session cookie).
  async function onCopyFolder(c: PortalClient) {
    const url = opsFolderUrl(OPS_WEB_BASE, c.username);
    try {
      await navigator.clipboard?.writeText(url);
      setCopiedFolder(c.username);
      window.setTimeout(() => setCopiedFolder((v) => (v === c.username ? null : v)), 2500);
    } catch { setNote(`Couldn’t copy — the folder link is ${url}`); }
  }

  async function copyTemp() {
    if (!cred) return;
    try {
      await navigator.clipboard?.writeText(cred.temp);
      setCopiedTemp(true); window.setTimeout(() => setCopiedTemp(false), 2000);
    } catch { /* visible on screen to copy by hand */ }
  }

  async function copyStoredCode(c: PortalClient) {
    if (!c.temp_code) return;
    try {
      await navigator.clipboard?.writeText(c.temp_code);
      setCopiedStoredCode(c.username);
      window.setTimeout(() => setCopiedStoredCode((v) => (v === c.username ? null : v)), 2000);
    } catch { setNote("Couldn’t copy — use Reveal and copy it by hand."); }
  }

  async function copyCredLink() {
    if (!cred?.onboard?.link) return;
    try {
      await navigator.clipboard?.writeText(cred.onboard.link);
      setCopiedCredLink(true); window.setTimeout(() => setCopiedCredLink(false), 2000);
    } catch { /* visible on screen to copy by hand */ }
  }

  // Onboard success hands the code + agreement result UP here, so it renders anchored
  // to the new client's roster row (connected to WHO it's for) instead of a
  // disconnected panel by the form whose only button was Cancel.
  function onCreated(r: { username: string; code?: string; link?: string; email: "sent" | "failed" | "none" }) {
    setConfirmReset(null); setConfirmEnable(null); setConfirmArchive(null);
    setCopiedTemp(false); setCopiedCredLink(false);
    if (r.code) {
      setCred({ username: r.username, temp: r.code, emailOk: false,
                onboard: { link: r.link ?? null, email: r.email } });
    }
    // Open the new client's rail so its one-time code is visible right where it's issued.
    setSelected(r.username);
    setNote(`✓ Created ${r.username} — its one-time code is in its detail panel.`);
    reload();
  }

  // Map an ActionKey to its control, reusing the existing handlers + disabled logic.
  // `variant` picks the oxblood primary button vs a compact secondary chip.
  function renderAction(key: ActionKey, c: PortalClient, variant: "primary" | "chip" = "chip"): React.ReactNode {
    const cls = variant === "primary"
      ? `operator-act-primary${key === "openFolder" ? " ghost" : ""}`
      : `operator-act-chip${key === "archive" ? " operator-act-danger" : ""}`;
    switch (key) {
      case "invite":
        return <button key="invite" type="button" className={cls} disabled={credBusy}
          onClick={() => onInvite(c)}>{c.invite_sent_at ? "Resend invite" : "Send invite"}</button>;
      case "copyUsername":
        return <button key="copyUsername" type="button" className={cls}
          onClick={() => onCopyUsername(c)}>{copied === c.username ? "Copied ✓" : "Copy username"}</button>;
      case "openFolder":
        return OPS_WEB_BASE ? <button key="openFolder" type="button" className={cls}
          onClick={() => onCopyFolder(c)}
          title="Copies the SFTPGo folder link — paste it into a tab where you're logged in as portal-ops">
          {copiedFolder === c.username ? "Copied ✓ — paste in SFTPGo" : "Copy folder link"}</button> : null;
      case "resendAgreement":
        return <button key="resendAgreement" type="button" className={cls} disabled={busy === c.username}
          onClick={() => onResendAgreement(c)}>Resend agreement</button>;
      case "copyLink":
        return <button key="copyLink" type="button" className={cls} disabled={!c.agreement_url}
          onClick={() => onCopyAgreement(c)}>
          {copiedAgr === c.username ? "Copied ✓" : variant === "primary" ? "Copy agreement link" : "Copy link"}</button>;
      case "newCode":
        return <button key="newCode" type="button" className={cls} disabled={credBusy}
          onClick={() => onNewCode(c)}>New code</button>;
      case "enable":
        return <button key="enable" type="button" className={cls} disabled={busy === c.username}
          onClick={() => onEnableClick(c)}>Enable</button>;
      case "restore":
        return <button key="restore" type="button" className={cls} disabled={busy === c.username}
          onClick={() => onRestore(c)}>{busy === c.username ? "Restoring…" : "Restore"}</button>;
      case "archive":
        return <button key="archive" type="button" className={cls} disabled={busy === c.username}
          onClick={() => setConfirmArchive(c)}>Archive</button>;
      default:
        return null;
    }
  }

  return (
    <>
      <PanelChrome<PortalClient>
        title="Clients"
        subtitle="Onboarding creates the account (locked), shows a one-time code, and emails the client their agreement link. At that link they enter the code, sign, and choose their password — which unlocks the account."
        state={state} onReload={reload} empty="No portal clients yet."
        bodyPreface={
          <>
            <PortalOnboardForm server={server} token={token} onCreated={onCreated}
              blocked={credBusy}
              existingUsernames={state.kind === "ready" ? state.data.map((c) => c.username) : []} />
            {note && <div className="operator-note">{note}</div>}
          </>
        }
        renderTable={(rows) => {
          const sel = rows.find((c) => c.username === selected) ?? rows[0];
          return (
            <div className="operator-clients-md">
              <div className="operator-clients-master">
                <div className="operator-clients-mhead">
                  <span>Client</span><span>Access · files</span>
                </div>
                {rows.map((c) => {
                  const name = c.label && c.label !== c.username ? c.label : c.username;
                  const subUser = c.label && c.label !== c.username ? c.username : null;
                  const pill = portalPill(c);
                  const dots = portalAccessTimeline(c).slice(0, 4);
                  return (
                    <button type="button" key={c.username}
                      className={`operator-crow${c.username === sel?.username ? " sel" : ""}`}
                      onClick={() => setSelected(c.username)}>
                      <span className="operator-crow-id">
                        <span className="operator-crow-nm">{name}</span>
                        {subUser && <span className="operator-crow-user">{subUser}</span>}
                      </span>
                      <span className={`operator-pill operator-pill-${pill.cls}`}>{pill.text}</span>
                      <span className="operator-crow-access">
                        <span className="operator-dots">
                          {dots.map((s) => (
                            <i key={s.key} className={`operator-dot${s.state === "done" ? " on" : s.state === "warn" ? " warn" : ""}`} />
                          ))}
                        </span>
                        <span className="operator-crow-up">{c.uploads_count}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
              <aside className="operator-clients-detail">
                {sel && (() => {
                  const groups = portalActionGroups(sel);
                  const access = portalAccessProgress(sel);
                  const showConfirm = confirmReset?.username === sel.username;
                  const showEnable = confirmEnable?.username === sel.username;
                  const showArchive = confirmArchive?.username === sel.username;
                  return (
                    <div className="operator-crail">
                      <div className="operator-crail-top">
                        <div className="operator-crail-idblock">
                          <div className="operator-crail-name">{sel.label || sel.username}</div>
                          <div className="operator-crail-user">
                            <span>{sel.username}</span>
                            <button type="button" className="operator-crail-copy"
                              onClick={() => onCopyUsername(sel)} title="Copy username">
                              {copied === sel.username ? "Copied ✓" : "Copy"}</button>
                          </div>
                          <div className="operator-crail-email">{sel.client_email || "no email on file"}</div>
                        </div>
                        <span className={`operator-pill operator-pill-${portalPill(sel).cls}`}>{portalPill(sel).text}</span>
                      </div>

                      {/* An issued code for ANOTHER client is still on screen — that's
                          why this client's issuing buttons are disabled. Without this
                          banner the guard is invisible (Rich hit it on 4simons-test:
                          "can't hit New code — no aff"). */}
                      {cred && cred.username !== sel.username && (
                        <div className="operator-crail-alert warn">
                          A one-time code for <strong>{cred.username}</strong> is still showing — copy it and dismiss that card before issuing anything here.
                        </div>
                      )}
                      {sel.status === "locked" && !sel.client_email && (
                        <div className="operator-crail-alert warn">No email on file — relay the one-time code yourself by text or call.</div>
                      )}
                      {access?.tone === "warn" && (
                        <div className="operator-crail-alert info">Signed in but hasn’t set a password — the one-time code is still their only credential.</div>
                      )}

                      <div className="operator-crail-label">Access</div>
                      <ul className="operator-tl">
                        {portalAccessTimeline(sel).map((s) => (
                          <li key={s.key}>
                            <span className="operator-tl-node"><span className={`operator-tl-mk ${s.state}`} /></span>
                            <span className={`operator-tl-step${s.state === "pending" ? " pend" : ""}`}>{s.label}</span>
                            <span className="operator-tl-when">{s.when || ""}</span>
                          </li>
                        ))}
                      </ul>

                      {/* The one-time code section renders whenever a code MATTERS —
                          the client hasn't set their own password yet (so a temp code is,
                          or will be, their only credential) — not only when one is stored.
                          A client whose code was issued before codes were kept (pre-v21)
                          gets an explicit "not on record" state pointing at New code;
                          silent absence read as a missing feature (Rich, 2026-07-13).
                          Hidden while the issue-time cred card for this client is up —
                          that card already shows the same code big. Masked until Reveal;
                          Copy works without revealing (relay by paste, nothing on screen). */}
                      {sel.status !== "archived" && !sel.password_set &&
                        !(cred && cred.username === sel.username) && (
                        <>
                          <div className="operator-crail-label">One-time code</div>
                          {sel.temp_code ? (
                            <div className="operator-crail-code">
                              <code>
                                {revealedCodeFor === sel.username ? sel.temp_code : "•••• ••••"}
                              </code>
                              <button type="button"
                                onClick={() => setRevealedCodeFor((v) => (v === sel.username ? null : sel.username))}>
                                {revealedCodeFor === sel.username ? "Hide" : "Reveal"}</button>
                              <button type="button" onClick={() => copyStoredCode(sel)}>
                                {copiedStoredCode === sel.username ? "Copied ✓" : "Copy"}</button>
                              <div className="operator-crail-code-hint">
                                Unused — they enter it at their agreement link; clears on its own once they finish. Relay by text or call, never email.
                              </div>
                            </div>
                          ) : (
                            <div className="operator-crail-code">
                              <span className="operator-crail-code-none">Not on record</span>
                              <div className="operator-crail-code-hint">
                                This client’s code was issued before codes were kept.
                                <strong> New code</strong> replaces it with one that stays here until used.
                              </div>
                            </div>
                          )}
                        </>
                      )}

                      <div className="operator-crail-label">Files</div>
                      <div className="operator-crail-files">
                        <span className="operator-crail-filecount">
                          {sel.uploads_count} {sel.uploads_count === 1 ? "file" : "files"}</span>
                      </div>

                      <div className="operator-crail-label">Actions</div>
                      <div className="operator-crail-acts">
                        {groups.primary && renderAction(groups.primary, sel, "primary")}
                        {groups.recovery.length > 0 && (
                          <div className="operator-act-group">
                            <div className="operator-act-grouplabel">Recovery &amp; access</div>
                            <div className="operator-act-chips">{groups.recovery.map((k) => renderAction(k, sel))}</div>
                          </div>
                        )}
                        {groups.lifecycle.length > 0 && (
                          <div className="operator-act-group">
                            <div className="operator-act-grouplabel">Lifecycle</div>
                            <div className="operator-act-chips">{groups.lifecycle.map((k) => renderAction(k, sel))}</div>
                          </div>
                        )}
                      </div>

                      {showConfirm && (
                        <div className="operator-confirm">
                          {sel.password_set ? (
                            <span>Send invite to <strong>{sel.username}</strong>? This resets their
                              account to a new one-time code — <strong>the password they chose stops
                              working</strong>. Only do this if they can no longer sign in.</span>
                          ) : (
                            <span>Resend invite to <strong>{sel.username}</strong>? This makes a new
                              one-time code and re-sends the invite — their current code stops working.</span>
                          )}
                          <span className="operator-confirm-actions">
                            <button type="button" className="operator-provision-btn"
                              disabled={busy === sel.username} onClick={() => doInvite(sel)}>
                              {busy === sel.username ? "Working…" : sel.password_set ? "Reset & send" : "Resend"}</button>
                            <button type="button" onClick={() => setConfirmReset(null)}>Cancel</button>
                          </span>
                        </div>
                      )}
                      {showEnable && (
                        <div className="operator-confirm">
                          <span><strong>{sel.username}</strong> hasn’t signed the agreement yet.
                            Enabling now opens their account <em>without</em> a recorded signature —
                            only do this if you’ve confirmed their consent another way. Prefer
                            <strong> Resend agreement</strong> so they sign.</span>
                          <span className="operator-confirm-actions">
                            <button type="button" className="operator-provision-btn"
                              disabled={busy === sel.username} onClick={() => onEnable(sel)}>
                              {busy === sel.username ? "Working…" : "Enable anyway"}</button>
                            <button type="button" onClick={() => setConfirmEnable(null)}>Cancel</button>
                          </span>
                        </div>
                      )}
                      {showArchive && (
                        <div className="operator-confirm">
                          <span>Archive <strong>{sel.username}</strong>? Their login is disabled and
                            the account is set aside. The encrypted data is kept — this is not a
                            delete, and Restore brings them back.</span>
                          <span className="operator-confirm-actions">
                            <button type="button" className="operator-archive-btn"
                              disabled={busy === sel.username} onClick={() => onArchive(sel)}>
                              {busy === sel.username ? "Working…" : "Archive"}</button>
                            <button type="button" onClick={() => setConfirmArchive(null)}>Cancel</button>
                          </span>
                        </div>
                      )}
                      {cred && cred.username === sel.username && (
                        <div className="operator-cred">
                          {/* ① The one-time CODE — relayed by the operator out-of-band. */}
                          <div className="operator-cred-chan">
                            <span className="operator-cred-num">1</span>
                            <div className="operator-cred-body">
                              <div className="operator-cred-line">
                                <span className="operator-cred-label">One-time code</span>
                                <code>{cred.temp}</code>
                                <button type="button" onClick={copyTemp}>{copiedTemp ? "Copied ✓" : "Copy"}</button>
                              </div>
                              <div className="operator-cred-hint">
                                Relay by <strong>text or call — never email</strong>. Until they use it,
                                the code stays on this client’s row — no need to rotate just to re-read it.
                              </div>
                            </div>
                          </div>
                          {/* ② The agreement LINK — the channel the CLIENT gets (auto-emailed, or
                              sent by the operator). Only for a just-onboarded client. */}
                          {cred.onboard && (
                            <div className="operator-cred-chan">
                              <span className="operator-cred-num">2</span>
                              <div className="operator-cred-body">
                                <div className="operator-cred-emailstate">
                                  {cred.onboard.email === "sent" && <>✓ Agreement link emailed to the client.</>}
                                  {cred.onboard.email === "failed" && <>⚠ The agreement email didn’t send — send this link yourself:</>}
                                  {cred.onboard.email === "none" && <>No email on file — send the client this agreement link:</>}
                                  {cred.onboard.email === "manual" && <>Send the client their link too — <strong>the code only works there</strong>, not at the portal sign-in page:</>}
                                </div>
                                {cred.onboard.link && (
                                  <div className="operator-onboard-temp">
                                    <code>{cred.onboard.link}</code>
                                    <button type="button" onClick={copyCredLink}>{copiedCredLink ? "Copied ✓" : "Copy link"}</button>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                          {/* invite/New-code (no onboard block): note how (or whether) an email went. */}
                          {!cred.onboard && (
                            <div className="operator-cred-hint">
                              {cred.emailOk === null
                                ? "No email sent — relay this code yourself. "
                                : cred.emailOk
                                  ? "Invite email sent — it carries their secure link. "
                                  : "⚠ The invite email didn’t send — send their agreement link yourself (Copy link on this row). "}
                              They enter the code at their agreement link and choose a new
                              password there — it doesn’t work at the portal sign-in page.
                            </div>
                          )}
                          <div className="operator-cred-done">
                            <button type="button" className="operator-provision-btn"
                              onClick={() => setCred(null)}>Done</button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </aside>
            </div>
          );
        }}
      />
    </>
  );
}

// Onboard-field validation, mirrored from the server so the operator gets inline
// feedback instead of a 422 after submit. Username regex is the SAME pattern the
// cloud route (portal_routes._USERNAME_PATTERN) and the Mini (portal.USERNAME_RE)
// enforce — it becomes the SFTPGo username + folder name, so it can't carry spaces
// or capitals. Email is format-checked only when present (the field is optional).
export function validateOnboardFields(
  username: string,
  email: string,
): { usernameError: string | null; emailError: string | null } {
  const u = username.trim();
  const usernameError =
    u === ""
      ? null
      : /^[a-z0-9][a-z0-9._-]{1,63}$/.test(u)
        ? null
        : "Lowercase letters, numbers, and . _ - only — no spaces or capitals. 2–64 characters, starting with a letter or number.";
  const e = email.trim();
  const emailError =
    e === "" || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)
      ? null
      : "Enter a valid email address, or leave it blank.";
  return { usernameError, emailError };
}

// Derive a valid SFTPGo username from an email local-part, so the operator
// usually doesn't have to invent one. Best-effort — the inline validator still
// gates anything malformed, and the operator can always override.
export function slugFromEmail(email: string): string {
  const local = (email.split("@")[0] || "").toLowerCase();
  return local.replace(/[^a-z0-9._-]+/g, "-").replace(/^[-._]+/, "").slice(0, 64);
}

// The onboard result handed UP to ClientsPanel, which renders it anchored to the new
// client's roster row (the code + agreement link), not a disconnected panel here.
type OnboardCreated = { username: string; code?: string; link?: string; email: "sent" | "failed" | "none" };

function PortalOnboardForm({
  server, token, onCreated, existingUsernames, blocked = false,
}: {
  server: string; token: string; onCreated: (r: OnboardCreated) => void;
  existingUsernames: string[]; blocked?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(""); const [username, setUsername] = useState("");
  const [label, setLabel] = useState(""); const [usernameTouched, setUsernameTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { usernameError, emailError } = validateOnboardFields(username, email);
  // Inline uniqueness: the roster is already loaded, so warn as-you-type instead of
  // letting a duplicate reach the server and bounce as an error.
  const usernameTaken = username.trim() !== "" && existingUsernames.includes(username.trim());
  // `blocked` = a one-time code is still displayed elsewhere; don't let a new onboard
  // clobber it (onCreated would replace the shown code before it's copied).
  const canSubmit = username.trim() !== "" && !usernameError && !usernameTaken
    && !emailError && !submitting && !blocked;

  function onEmailChange(v: string) {
    setEmail(v);
    if (!usernameTouched) setUsername(slugFromEmail(v));  // auto-suggest until edited
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true); setError(null);
    const u = username.trim(); const em = email.trim();
    try {
      // Locked-account-at-onboard: the account is created DISABLED and we get the
      // one-time login code (shown once) + the agreement link. If an email was
      // given the server auto-emails the agreement link; signing unlocks the account.
      const out = await operatorOnboardPortalClient(server, token, u, label.trim(), em);
      // Hand the result up so it anchors to the new client's roster row, and collapse
      // the form (a fresh "+ Onboard client" button returns for the next one) — the
      // old Cancel-only result panel is gone.
      onCreated({
        username: u, code: out.temp_password, link: out.agreement_url,
        email: !em ? "none" : out.email_status?.ok ? "sent" : "failed",
      });
      setEmail(""); setUsername(""); setLabel(""); setUsernameTouched(false); setOpen(false);
    } catch (err) { setError(portalError(err, "Couldn’t onboard the client.")); }
    finally { setSubmitting(false); }
  }

  if (!open) return (
    <button type="button" className="operator-approve operator-onboard-open" onClick={() => setOpen(true)}>
      + Onboard client
    </button>
  );
  return (
    <form className="operator-onboard-form" onSubmit={submit}>
      <h3 className="operator-onboard-title">Onboard a client</h3>
      <p className="operator-onboard-lede">
        Creates their secure account (locked until they sign) and shows you a
        one-time code to relay. If you add an email, we send them the agreement
        link automatically; signing unlocks the account.
      </p>
      <label className="operator-onboard-field">
        <span className="operator-onboard-label">Client email</span>
        <input type="email" value={email} onChange={(e) => onEmailChange(e.target.value)}
          autoFocus placeholder="name@example.com"
          aria-invalid={emailError ? true : undefined} />
        <span className="operator-onboard-hint">We email them the agreement link here. Leave blank to send the link yourself.</span>
        {emailError && <span className="operator-onboard-error">{emailError}</span>}
      </label>
      <label className="operator-onboard-field">
        <span className="operator-onboard-label">Username</span>
        <input value={username}
          onChange={(e) => { setUsername(e.target.value); setUsernameTouched(true); }}
          required placeholder="e.g. smith-2026"
          aria-invalid={usernameError || usernameTaken ? true : undefined} />
        <span className="operator-onboard-hint">
          Auto-filled from the email — edit if you like. Becomes their login and
          folder name; can’t be changed later.
        </span>
        {usernameError && <span className="operator-onboard-error">{usernameError}</span>}
        {!usernameError && usernameTaken && (
          <span className="operator-onboard-error">That username is already taken — choose a different one.</span>
        )}
      </label>
      <label className="operator-onboard-field">
        <span className="operator-onboard-label">Label <span className="operator-onboard-opt">optional</span></span>
        <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Smith — pilot" />
        <span className="operator-onboard-hint">A human name for the roster; not shown to the client.</span>
      </label>
      <div className="operator-onboard-actions">
        <button type="submit" className="operator-approve" disabled={!canSubmit}>
          {submitting ? "Working…" : "Create account & get code"}
        </button>
        <button type="button" className="operator-onboard-cancel"
          onClick={() => { setOpen(false); setError(null); }}>Close</button>
      </div>
      {blocked && (
        <span className="operator-onboard-hint">
          Copy and dismiss the one-time code shown on the roster before onboarding another client.
        </span>
      )}
      {error && <div className="operator-note">{error}</div>}
    </form>
  );
}

function ApplicationsPanel({ server, token }: { server: string; token: string }) {
  const loader = useCallback(
    () => operatorListApplications(server, token).then((r) => r.applications),
    [server, token],
  );
  const { state, reload } = useLoader<OperatorApplication[]>(loader);
  // Approving sends the access-code email, so the panel needs to know whether
  // email is actually deliverable — to warn before, and report honestly after.
  const settingsLoader = useCallback(
    () => operatorGetSettings(server, token),
    [server, token],
  );
  const { state: settingsState, reload: reloadSettings } =
    useLoader<OperatorSettings>(settingsLoader);
  const sendBlocked =
    settingsState.kind === "ready" ? emailBlocked(settingsState.data.email) : false;
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [approved, setApproved] = useState<ApproveBanner | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  // Default to the actionable subset — pending applications need operator
  // review. The dropdown still offers "All" to see approved/declined.
  const [statusFilter, setStatusFilter] = useState<AppStatusFilter>("pending");
  const [roleFilter, setRoleFilter] = useState<string>("all");
  const [search, setSearch] = useState("");

  function toggleExpand(id: string) {
    setExpandedId((prev) => (prev === id ? null : id));
  }

  const filtersActive =
    statusFilter !== "all" || roleFilter !== "all" || search.trim() !== "";

  // useMemo so filter+search-input keystrokes don't re-walk the full
  // applications list twice per render (table + count). Cheap at pilot
  // volume, but free with one wrapper line.
  const filteredApplications = useMemo(() => {
    if (state.kind !== "ready") return [] as OperatorApplication[];
    const q = search.trim().toLowerCase();
    return state.data.filter((a) => {
      if (statusFilter !== "all" && a.status !== statusFilter) return false;
      if (roleFilter !== "all" && a.role !== roleFilter) return false;
      if (q) {
        const hay = `${a.name ?? ""} ${a.email}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [state, statusFilter, roleFilter, search]);

  async function handleApprove(a: OperatorApplication, comp: boolean) {
    if (busy === a.id) return;
    const billing = comp
      ? "free (grant-funded — no payment)"
      : "paid (Stripe)";
    // Warn + prevent the silent send: if email won't actually go out, the
    // confirm makes that explicit before the operator commits.
    const confirmMsg = sendBlocked
      ? `Email sending is OFF (fake-email mode or no Resend key). A ${billing} code will be minted for ${a.email} but NOT emailed — you'll have to send it manually. Mint anyway?`
      : `Approve ${a.email} as ${billing}? A fresh access code will be minted and emailed.`;
    if (!window.confirm(confirmMsg)) return;
    setBusy(a.id);
    setActionError(null);
    try {
      const res: ApproveApplicationResponse =
        await operatorApproveApplication(server, token, a.id, comp);
      setApproved({
        code: res.access_code?.code ?? "(no code returned — see server log)",
        email: res.application.email,
        emailOk: res.email_status.ok,
        emailError: res.email_status.error,
        faked: res.email_status.faked ?? false,
        noop: res.email_status.noop ?? false,
      });
      reload();
      reloadSettings();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  async function handleDecline(a: OperatorApplication) {
    if (busy === a.id) return;
    setBusy(a.id);
    setActionError(null);
    if (!window.confirm(`Decline ${a.email}? No email will be sent.`)) {
      setBusy(null);
      return;
    }
    try {
      await operatorDeclineApplication(server, token, a.id);
      reload();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  // Set aside (on=true) or return to pending (on=false). Reversible, no email,
  // no code — so no confirm; the operator can flip it straight back.
  async function handleConcierge(a: OperatorApplication, on: boolean) {
    if (busy === a.id) return;
    setBusy(a.id);
    setActionError(null);
    try {
      await operatorSetApplicationConcierge(server, token, a.id, on);
      reload();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="operator-panel">
      <div className="operator-panel-head">
        <div>
          <h2>Applications</h2>
          <p className="muted">Pilot applicants (newest first)</p>
        </div>
        <button
          type="button"
          className="operator-reload"
          onClick={reload}
          disabled={state.kind === "loading"}
        >
          {state.kind === "loading" ? "Loading…" : "Reload"}
        </button>
      </div>

      {sendBlocked && (
        <div className="operator-email-blocked" role="status">
          <strong>
            {settingsState.kind === "ready" &&
            settingsState.data.email.use_fake_email.value
              ? "Fake email is ON — approval codes are NOT emailed."
              : "No Resend key on this server — approval codes are NOT emailed."}
          </strong>
          <span>
            You can still approve (the code is minted), but you’ll have to send
            it to the applicant yourself. Turn this off in the Email tab to send
            automatically.
          </span>
        </div>
      )}

      {approved && (
        <div
          className={`operator-approve-banner${approved.faked || (!approved.emailOk && !approved.noop) ? " operator-approve-banner-warn" : ""}`}
        >
          {approved.faked ? (
            <>
              <strong>Code minted — NOT emailed.</strong>{" "}
              Fake-email mode is on, so nothing was sent. Copy{" "}
              <code>{approved.code}</code> and send it to {approved.email}{" "}
              yourself.
            </>
          ) : approved.noop ? (
            <>
              <strong>Already approved.</strong> Code <code>{approved.code}</code>{" "}
              was issued to {approved.email} earlier — not re-sent.
            </>
          ) : approved.emailOk ? (
            <>
              <strong>Approved.</strong> Code <code>{approved.code}</code> emailed
              to {approved.email}.
            </>
          ) : (
            <>
              <strong>Code minted — email failed.</strong>{" "}
              {approved.emailError ?? "unknown error"}. Copy{" "}
              <code>{approved.code}</code> and send it to {approved.email}{" "}
              manually.
            </>
          )}{" "}
          <button
            type="button"
            onClick={() => setApproved(null)}
            style={{ marginLeft: 8 }}
          >
            Dismiss
          </button>
        </div>
      )}

      <div className="operator-filters">
        <label>
          <span>Status</span>
          <select
            value={statusFilter}
            onChange={(e) =>
              setStatusFilter(e.target.value as AppStatusFilter)
            }
          >
            <option value="all">All</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="declined">Declined</option>
            <option value="concierge">Concierge</option>
          </select>
        </label>
        <label>
          <span>Role</span>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
          >
            <option value="all">All</option>
            <option value="pro_se">Pro se</option>
            <option value="attorney">Attorney</option>
            <option value="advocate">Advocate</option>
            <option value="court_appointed">Court-appointed</option>
            <option value="researcher">Researcher</option>
            <option value="other">Other</option>
          </select>
        </label>
        <label className="operator-filter-search">
          <span>Search</span>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="name or email"
          />
        </label>
        {filtersActive && (
          <button
            type="button"
            className="operator-filter-clear"
            onClick={() => {
              setStatusFilter("all");
              setRoleFilter("all");
              setSearch("");
              setExpandedId(null);
            }}
          >
            Clear
          </button>
        )}
      </div>

      {actionError && (
        <div className="operator-error">Action failed: {actionError}</div>
      )}
      {state.kind === "error" && (
        <div className="operator-error">Could not load: {state.message}</div>
      )}
      {state.kind === "ready" && state.data.length === 0 && (
        <div className="operator-empty">No applications yet.</div>
      )}
      {state.kind === "ready" &&
        state.data.length > 0 &&
        (filteredApplications.length === 0 ? (
          <div className="operator-empty">
            No applications match the current filters.
          </div>
        ) : (
          <>
            {filtersActive && (
              <div className="operator-filter-count">
                Showing {filteredApplications.length} of {state.data.length}
              </div>
            )}
            <ApplicationsTable
              rows={filteredApplications}
              expandedId={expandedId}
              toggleExpand={toggleExpand}
              handleApprove={handleApprove}
              handleDecline={handleDecline}
              handleConcierge={handleConcierge}
              busy={busy}
            />
          </>
        ))}

      {state.kind === "loading" && (
        <div className="operator-loading">Loading…</div>
      )}
    </div>
  );
}


type ApplicationsTableProps = {
  rows: OperatorApplication[];
  expandedId: string | null;
  toggleExpand: (id: string) => void;
  handleApprove: (a: OperatorApplication, comp: boolean) => void;
  handleDecline: (a: OperatorApplication) => void;
  handleConcierge: (a: OperatorApplication, on: boolean) => void;
  busy: string | null;
};

function ApplicationsTable({
  rows,
  expandedId,
  toggleExpand,
  handleApprove,
  handleDecline,
  handleConcierge,
  busy,
}: ApplicationsTableProps) {
  return (
    <table className="operator-table operator-table-applications">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              <th>Timing</th>
              <th>Sources</th>
              <th>App status</th>
              <th>Submitted</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((a) => {
              const isExpanded = expandedId === a.id;
              return (
                <Fragment key={a.id}>
                  <tr
                    className={
                      "operator-row" + (isExpanded ? " is-expanded" : "")
                    }
                    onClick={() => toggleExpand(a.id)}
                  >
                    <td>
                      <span className="operator-row-name">
                        {a.name ?? <em className="muted">(no name)</em>}
                      </span>
                      {a.concierge_candidate && (
                        <span
                          className="operator-chip operator-chip-concierge"
                          title="Multi-source or unsupported export — concierge candidate"
                        >
                          concierge?
                        </span>
                      )}
                    </td>
                    <td>{a.email}</td>
                    <td>{ROLE_LABEL[a.role] ?? a.role}</td>
                    <td>
                      {a.case_status
                        ? CASE_STATUS_LABEL[a.case_status] ?? a.case_status
                        : "—"}
                    </td>
                    <td>
                      {a.hearing_window ? (
                        <span className={timingChipClass(a.hearing_window)}>
                          {HEARING_WINDOW_LABEL[a.hearing_window] ??
                            a.hearing_window}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>{summarizeSources(a.sources)}</td>
                    <td>
                      <span className={`badge badge-${a.status}`}>
                        {a.status}
                      </span>
                    </td>
                    <td>{formatLocalTime(a.created_at)}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      {(a.status === "pending" ||
                        a.status === "concierge") && (
                        <div className="operator-row-actions">
                          <button
                            type="button"
                            className="operator-approve"
                            onClick={() => handleApprove(a, true)}
                            disabled={busy === a.id}
                            title="Approve and mark this code free (grant-funded — no payment)"
                          >
                            {busy === a.id ? "Working…" : "Approve · free"}
                          </button>
                          <button
                            type="button"
                            className="operator-approve operator-approve-paid"
                            onClick={() => handleApprove(a, false)}
                            disabled={busy === a.id}
                            title="Approve as a paid (Stripe) code"
                          >
                            {busy === a.id ? "Working…" : "Approve · paid"}
                          </button>
                          <button
                            type="button"
                            className="operator-decline"
                            onClick={() => handleDecline(a)}
                            disabled={busy === a.id}
                          >
                            Decline
                          </button>
                          {a.status === "pending" ? (
                            <button
                              type="button"
                              className="operator-concierge"
                              onClick={() => handleConcierge(a, true)}
                              disabled={busy === a.id}
                              title="Set aside to handle personally — leaves the pending queue but stays approvable"
                            >
                              Concierge
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="operator-concierge operator-concierge-unset"
                              onClick={() => handleConcierge(a, false)}
                              disabled={busy === a.id}
                              title="Return this application to the pending queue"
                            >
                              ↩ Pending
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className="operator-detail-row">
                      <td colSpan={APPLICATIONS_COL_COUNT}>
                        <ApplicationDetail app={a} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
  );
}


function ApplicationDetail({ app }: { app: OperatorApplication }) {
  const sources = app.sources ?? [];
  const isProfessional =
    app.role === "attorney" || app.role === "court_appointed";
  const details = app.details ?? {};
  return (
    <div className="operator-detail">
      <dl className="operator-detail-grid">
        <dt>Sources</dt>
        <dd>
          {sources.length > 0
            ? sources.map((s) => SOURCE_LABEL[s] ?? s).join(", ")
            : "—"}
        </dd>

        <dt>Time span</dt>
        <dd>
          {app.time_span
            ? TIME_SPAN_LABEL[app.time_span] ?? app.time_span
            : "—"}
        </dd>

        {app.role === "pro_se" && (
          <>
            <dt>Working with anyone?</dt>
            <dd>
              {app.support_status
                ? SUPPORT_STATUS_LABEL[app.support_status] ?? app.support_status
                : "—"}
            </dd>
          </>
        )}

        {isProfessional && (
          <>
            <dt>Specific case now?</dt>
            <dd>
              {app.active_case_status
                ? ACTIVE_CASE_LABEL[app.active_case_status] ??
                  app.active_case_status
                : "—"}
            </dd>
            <dt>Firm or organization</dt>
            <dd>{details.firm_and_jurisdiction ?? "—"}</dd>
          </>
        )}

        <dt>How they heard</dt>
        <dd>{details.how_heard ?? "—"}</dd>

        <dt>Notes (free-form)</dt>
        <dd>
          {app.free_form_notes ? (
            <pre className="operator-detail-notes">{app.free_form_notes}</pre>
          ) : (
            "—"
          )}
        </dd>
      </dl>
    </div>
  );
}


type CodeStatusFilter = "all" | "active" | "used" | "revoked";

function AccessCodesPanel({ server, token }: { server: string; token: string }) {
  const loader = useCallback(
    () => operatorListAccessCodes(server, token).then((r) => r.access_codes),
    [server, token],
  );
  const { state, reload } = useLoader<OperatorAccessCode[]>(loader);
  const [showMint, setShowMint] = useState(false);
  const [justMinted, setJustMinted] = useState<OperatorAccessCode[] | null>(null);
  const [revoking, setRevoking] = useState<string | null>(null);
  const [revokeError, setRevokeError] = useState<string | null>(null);
  const [resending, setResending] = useState<string | null>(null);
  const [resendNote, setResendNote] = useState<string | null>(null);
  const [compToggling, setCompToggling] = useState<string | null>(null);
  // Default to the codes that matter day-to-day — Active (ready/in use). The
  // dropdown still offers "All" to see used/revoked.
  const [statusFilter, setStatusFilter] = useState<CodeStatusFilter>("active");
  const [search, setSearch] = useState("");

  const filtersActive = statusFilter !== "all" || search.trim() !== "";

  const filteredAccessCodes = useMemo(() => {
    if (state.kind !== "ready") return [] as OperatorAccessCode[];
    const q = search.trim().toLowerCase();
    return state.data.filter((c) => {
      if (statusFilter !== "all" && c.status !== statusFilter) return false;
      if (q && !c.code.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [state, statusFilter, search]);

  async function handleRevoke(c: OperatorAccessCode) {
    if (!window.confirm(`Revoke ${c.code}? This cannot be undone.`)) {
      return;
    }
    setRevoking(c.id);
    setRevokeError(null);
    try {
      await operatorRevokeAccessCode(server, token, c.id);
      reload();
    } catch (e) {
      setRevokeError(e instanceof Error ? e.message : String(e));
    } finally {
      setRevoking(null);
    }
  }

  async function handleResend(c: OperatorAccessCode) {
    setResending(c.id);
    setResendNote(null);
    try {
      const r = await operatorResendAccessCodeEmail(server, token, c.id);
      const to = c.applicant_email ?? c.email ?? "the linked address";
      setResendNote(
        r.email_status.ok
          ? r.email_status.faked
            ? `Email is in test mode — ${c.code} was logged, not sent.`
            : `Code email re-sent to ${to}.`
          : `Send failed: ${r.email_status.error ?? "unknown error"}`,
      );
    } catch (e) {
      setResendNote(
        `Send failed: ${e instanceof Error ? e.message : String(e)}`,
      );
    } finally {
      setResending(null);
    }
  }

  async function handleSetComp(c: OperatorAccessCode, comp: boolean) {
    setCompToggling(c.id);
    setRevokeError(null);
    setResendNote(null);
    try {
      await operatorSetAccessCodeComp(server, token, c.id, comp);
      reload();
      // The approval email's free/paid line is now stale — offer to resend the
      // corrected one (the server re-renders it from the code's current comp).
      const to = c.applicant_email ?? c.email;
      if (
        to &&
        window.confirm(
          `${c.code} is now ${comp ? "free (comped)" : "paid"}. ` +
            `Resend the updated email to ${to}?`,
        )
      ) {
        const r = await operatorResendAccessCodeEmail(server, token, c.id);
        setResendNote(
          r.email_status.ok
            ? r.email_status.faked
              ? `Email is in test mode — ${c.code} was logged, not sent.`
              : `Updated email re-sent to ${to}.`
            : `Resend failed: ${r.email_status.error ?? "unknown error"}`,
        );
      }
    } catch (e) {
      setRevokeError(
        `Couldn't change billing: ${e instanceof Error ? e.message : String(e)}`,
      );
    } finally {
      setCompToggling(null);
    }
  }

  function handleMinted(codes: OperatorAccessCode[]) {
    setJustMinted(codes);
    setShowMint(false);
    reload();
  }

  return (
    <div className="operator-panel">
      <div className="operator-panel-head">
        <div>
          <h2>Access codes</h2>
          <p className="muted">Codes minted for pilot users</p>
        </div>
        <div className="operator-panel-actions">
          <button
            type="button"
            className="operator-mint"
            onClick={() => setShowMint((v) => !v)}
          >
            {showMint ? "Cancel" : "Mint code"}
          </button>
          <button
            type="button"
            className="operator-reload"
            onClick={reload}
            disabled={state.kind === "loading"}
          >
            {state.kind === "loading" ? "Loading…" : "Reload"}
          </button>
        </div>
      </div>

      {showMint && (
        <MintForm server={server} token={token} onMinted={handleMinted} />
      )}

      {justMinted && justMinted.length > 0 && (
        <JustMintedBanner
          codes={justMinted}
          onDismiss={() => setJustMinted(null)}
        />
      )}

      <div className="operator-filters">
        <label>
          <span>Status</span>
          <select
            value={statusFilter}
            onChange={(e) =>
              setStatusFilter(e.target.value as CodeStatusFilter)
            }
          >
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="used">Used</option>
            <option value="revoked">Revoked</option>
          </select>
        </label>
        <label className="operator-filter-search">
          <span>Search</span>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="code"
          />
        </label>
        {filtersActive && (
          <button
            type="button"
            className="operator-filter-clear"
            onClick={() => {
              setStatusFilter("all");
              setSearch("");
            }}
          >
            Clear
          </button>
        )}
      </div>

      {revokeError && (
        <div className="operator-error">Revoke failed: {revokeError}</div>
      )}
      {resendNote && (
        <div
          className={
            resendNote.startsWith("Send failed")
              ? "operator-error"
              : "operator-note"
          }
          role="status"
        >
          {resendNote}
        </div>
      )}
      {state.kind === "error" && (
        <div className="operator-error">Could not load: {state.message}</div>
      )}
      {state.kind === "ready" && state.data.length === 0 && (
        <div className="operator-empty">No access codes yet.</div>
      )}
      {state.kind === "ready" &&
        state.data.length > 0 &&
        (filteredAccessCodes.length === 0 ? (
          <div className="operator-empty">
            No access codes match the current filters.
          </div>
        ) : (
          <>
            {filtersActive && (
              <div className="operator-filter-count">
                Showing {filteredAccessCodes.length} of {state.data.length}
              </div>
            )}
            <table className="operator-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Status</th>
                  <th>Type</th>
                  <th>Created</th>
                  <th>Expires</th>
                  <th>Used</th>
                  <th>Applicant</th>
                  <th>Agreement</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredAccessCodes.map((c) => (
                  <tr key={c.id}>
                    <td><code>{c.code}</code></td>
                    <td>
                      <span className={`badge badge-${c.status}`}>
                        {c.status}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`badge ${c.comp ? "badge-comp" : "badge-paid"}`}
                      >
                        {c.comp ? "comp" : "paid"}
                      </span>
                    </td>
                    <td>{formatLocalTime(c.created_at)}</td>
                    <td>{formatLocalTime(c.expires_at)}</td>
                    <td>{formatLocalTime(c.used_at)}</td>
                    <td>
                      <OwnerCell
                        name={c.applicant_name}
                        email={c.applicant_email ?? c.email}
                      />
                    </td>
                    <td>
                      {c.agreement ? (
                        <span className="operator-agreement">
                          {c.agreement.version} ·{" "}
                          {formatLocalTime(c.agreement.accepted_at)}
                        </span>
                      ) : (
                        <span className="operator-agreement operator-agreement-missing">
                          none
                        </span>
                      )}
                    </td>
                    <td>
                      {c.status === "active" && (
                        <div className="operator-row-actions">
                          {(c.applicant_email ?? c.email) != null && (
                            <button
                              type="button"
                              className="operator-resend"
                              onClick={() => handleResend(c)}
                              disabled={resending === c.id}
                            >
                              {resending === c.id ? "Sending…" : "Resend email"}
                            </button>
                          )}
                          <button
                            type="button"
                            className="operator-comp-toggle"
                            onClick={() => handleSetComp(c, !c.comp)}
                            disabled={compToggling === c.id}
                            title={
                              c.comp
                                ? "Change to a paid (Stripe) code"
                                : "Change to a free (grant-funded) code"
                            }
                          >
                            {compToggling === c.id
                              ? "Saving…"
                              : c.comp
                                ? "Make paid"
                                : "Make free"}
                          </button>
                          <button
                            type="button"
                            className="operator-revoke"
                            onClick={() => handleRevoke(c)}
                            disabled={revoking === c.id}
                          >
                            {revoking === c.id ? "Revoking…" : "Revoke"}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        ))}
      {state.kind === "loading" && (
        <div className="operator-loading">Loading…</div>
      )}
    </div>
  );
}


function MintForm({
  server,
  token,
  onMinted,
}: {
  server: string;
  token: string;
  onMinted: (codes: OperatorAccessCode[]) => void;
}) {
  const [count, setCount] = useState(1);
  const [prefix, setPrefix] = useState("BETA");
  const [expiresInDays, setExpiresInDays] = useState<string>("");
  const [notes, setNotes] = useState("");
  const [email, setEmail] = useState("");
  const [comp, setComp] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const emailValid = email.trim() === "" || /^\S+@\S+\.\S+$/.test(email.trim());

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const body: Parameters<typeof operatorMintAccessCodes>[2] = {
        count,
        prefix,
      };
      if (expiresInDays.trim()) {
        body.expires_in_days = Number(expiresInDays);
      }
      if (notes.trim()) {
        body.notes = notes.trim();
      }
      if (email.trim()) {
        body.email = email.trim();
      }
      body.comp = comp;
      const res = await operatorMintAccessCodes(server, token, body);
      onMinted(res.access_codes);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form className="operator-mint-form" onSubmit={handleSubmit}>
      <label className="operator-mint-comp">
        <input
          type="checkbox"
          checked={comp}
          onChange={(e) => setComp(e.target.checked)}
        />
        <span>
          Comp — grant-funded, runs free on the pilot key (no payment, no
          personal key).{" "}
          <span className="operator-mint-comp-hint">
            Leave unchecked for a paid code: the user pays per analysis with
            their own key.
          </span>
        </span>
      </label>
      <label>
        <span>Count</span>
        <input
          type="number"
          min={1}
          max={100}
          value={count}
          onChange={(e) => setCount(Number(e.target.value) || 1)}
        />
      </label>
      <label>
        <span>Prefix</span>
        <input
          type="text"
          maxLength={8}
          value={prefix}
          onChange={(e) => setPrefix(e.target.value.toUpperCase())}
          placeholder="BETA"
        />
      </label>
      <label>
        <span>Expires (days)</span>
        <input
          type="number"
          min={1}
          max={3650}
          value={expiresInDays}
          onChange={(e) => setExpiresInDays(e.target.value)}
          placeholder="never"
        />
      </label>
      <label className="operator-mint-notes">
        <span>Notes (optional)</span>
        <input
          type="text"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </label>
      <label className="operator-mint-notes">
        <span>Notify email (optional)</span>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="who gets the ready/nudge emails"
          aria-invalid={!emailValid}
        />
      </label>
      <button type="submit" disabled={submitting || !emailValid}>
        {submitting ? "Minting…" : comp ? "Mint comp" : "Mint paid"}
      </button>
      {!emailValid && (
        <div className="operator-field-hint muted">Enter a valid email or leave it blank.</div>
      )}
      {error && <div className="operator-error">{error}</div>}
    </form>
  );
}


export function JustMintedBanner({
  codes,
  onDismiss,
}: {
  codes: OperatorAccessCode[];
  onDismiss: () => void;
}) {
  // Inline copy feedback — same idle/copied/failed + ~2.5s auto-reset shape as
  // CopyButton in RunState.tsx (the passphrase copy button). No global toast
  // system in this console, so a text swap on the button itself is the
  // register; a silent copyAll() gave no confirmation the click landed.
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">(
    "idle",
  );
  async function copyAll() {
    const text = codes.map((c) => c.code).join("\n");
    try {
      await navigator.clipboard.writeText(text);
      setCopyState("copied");
    } catch {
      // Clipboard rejected (insecure context / permission).
      setCopyState("failed");
    }
    window.setTimeout(() => setCopyState("idle"), 2500);
  }
  const copyLabel = (base: string) =>
    copyState === "copied" ? "Copied ✓" : copyState === "failed" ? "Copy failed" : base;
  if (codes.length === 1) {
    return (
      <div className="operator-mint-banner">
        <strong>Minted: <code>{codes[0].code}</code></strong>
        <div className="operator-mint-banner-actions">
          <button type="button" onClick={copyAll}>{copyLabel("Copy")}</button>
          <button type="button" onClick={onDismiss}>Dismiss</button>
        </div>
      </div>
    );
  }
  return (
    <div className="operator-mint-banner">
      <strong>Minted {codes.length} codes:</strong>
      <ul>
        {codes.map((c) => (
          <li key={c.id}><code>{c.code}</code></li>
        ))}
      </ul>
      <div className="operator-mint-banner-actions">
        <button type="button" onClick={copyAll}>{copyLabel("Copy all")}</button>
        <button type="button" onClick={onDismiss}>Dismiss</button>
      </div>
    </div>
  );
}


// Spawned + executing — EXCLUDES "queued" (a queued run isn't running yet, so
// Run history neither auto-polls nor offers to cancel it). Distinct from
// IN_FLIGHT_STATUSES (which includes "queued") used by the Live runs view + the
// nav badge, where a queued run IS shown and is cancellable.
const ACTIVE_RUN_STATUSES = new Set(["initializing", "running"]);

function RunsPanel({ server, token }: { server: string; token: string }) {
  const loader = useCallback(
    () => operatorListRuns(server, token).then((r) => r.runs),
    [server, token],
  );
  const { state, reload, reloadQuiet } = useLoader<OperatorRunRecord[]>(loader);
  // Live refresh while anything is running: the old reload-button-only
  // table showed a stale "running" until the operator thought to click.
  // Quiet poll (no loading flash) every 15s, only while a live run exists.
  const anyLive =
    state.kind === "ready" &&
    state.data.some((r) => ACTIVE_RUN_STATUSES.has(r.status));
  useEffect(() => {
    if (!anyLive) return;
    const t = setInterval(reloadQuiet, 15_000);
    return () => clearInterval(t);
  }, [anyLive, reloadQuiet]);
  // Cancel — operator kill-switch on a live row.
  const [cancelling, setCancelling] = useState<string | null>(null);
  function cancelRun(row: OperatorRunRecord) {
    const label = row.access_code ? `${row.cloud_run_id} (${row.access_code})` : row.cloud_run_id;
    if (!window.confirm(cancelRunConfirm(label))) {
      return;
    }
    setCancelling(row.cloud_run_id);
    operatorCancelRun(server, token, row.cloud_run_id)
      .then(() => reloadQuiet())
      .catch((e: unknown) => {
        window.alert(
          `Cancel failed: ${e instanceof Error ? e.message : String(e)}`,
        );
      })
      .finally(() => setCancelling(null));
  }
  // Receipt modal — operator clicks "View" on a row, we fetch the
  // receipt-endpoint payload (presently shape-identical to the list
  // row, but kept on the dedicated endpoint so a future divergence
  // doesn't require an op-dashboard rewrite).
  const [receiptOpen, setReceiptOpen] = useState<OperatorRunRecord | null>(null);
  const [receiptState, setReceiptState] = useState<LoadState<OperatorRunRecord>>(
    { kind: "idle" },
  );
  function viewReceipt(row: OperatorRunRecord) {
    setReceiptOpen(row);
    setReceiptState({ kind: "loading" });
    operatorGetRunReceipt(server, token, row.id)
      .then((r) => setReceiptState({ kind: "ready", data: r }))
      .catch((e: unknown) =>
        setReceiptState({
          kind: "error",
          message: e instanceof Error ? e.message : String(e),
        }),
      );
  }
  function closeReceipt() {
    setReceiptOpen(null);
    setReceiptState({ kind: "idle" });
  }
  return (
    <>
    <PanelChrome
      title="Run history"
      subtitle="All runs launched against this server. A run is recorded when the wizard launches with an access code; status flips to done/error when the chain completes. In-flight runs also appear under Live runs."
      state={state}
      onReload={reload}
      empty="No runs recorded yet."
      renderTable={(rows) => (
        <table className="operator-table">
          <thead>
            <tr>
              <th>Cloud run ID</th>
              <th>Status</th>
              <th>Started</th>
              <th>Completed</th>
              <th>Cost (USD)</th>
              <th>Access code</th>
              <th>Owner</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td><code>{r.cloud_run_id}</code></td>
                <td>{r.status}</td>
                <td>{formatLocalTime(r.started_at)}</td>
                <td>{formatLocalTime(r.completed_at)}</td>
                <td>
                  {r.cost_usd_anthropic !== null
                    ? `$${r.cost_usd_anthropic.toFixed(2)}`
                    : "—"}
                  {r.journey_preview_cost_usd != null && (
                    <span className="muted">
                      {" "}(incl. preview ${r.journey_preview_cost_usd.toFixed(2)})
                    </span>
                  )}
                </td>
                <td>
                  {r.access_code
                    ? <code>{r.access_code}</code>
                    : "—"}
                </td>
                <td><OwnerCell name={r.owner_name} email={r.owner_email ?? r.notify_email} /></td>
                <td>
                  <button
                    type="button"
                    className="operator-row-action"
                    onClick={() => viewReceipt(r)}
                  >
                    View
                  </button>
                  {ACTIVE_RUN_STATUSES.has(r.status) && (
                    <button
                      type="button"
                      className="operator-row-action operator-row-action--danger"
                      disabled={cancelling === r.cloud_run_id}
                      onClick={() => cancelRun(r)}
                    >
                      {cancelling === r.cloud_run_id ? "Cancelling…" : "Cancel"}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    />
    {receiptOpen && (
      <ReceiptModal
        row={receiptOpen}
        state={receiptState}
        onClose={closeReceipt}
      />
    )}
    </>
  );
}


// --- Metrics panel (Phase C) ---
//
// The pilot-meta analytical cuts the research registry derives offline,
// surfaced live in the operator console so Rich can read pricing /
// off-ramp / anomaly signal without running the metadata pull. Data is a
// single object (not a row list) so this uses its own chrome rather than
// PanelChrome, which assumes an array.

function usd(n: number | null | undefined): string {
  return n === null || n === undefined ? "—" : `$${n.toFixed(2)}`;
}

function num(n: number | null | undefined): string {
  return n === null || n === undefined ? "—" : String(n);
}

function MetricsPanel({ server, token }: { server: string; token: string }) {
  const loader = useCallback(
    () => operatorGetMetrics(server, token),
    [server, token],
  );
  const { state, reload } = useLoader<OperatorMetrics>(loader);
  return (
    <div className="operator-panel">
      <div className="operator-panel-head">
        <div>
          <h2>Metrics</h2>
          <p className="muted">
            Pilot-meta cuts computed live from content-free run records — the
            same off-ramp / pricing / words-per-message signal the research
            registry derives offline. No message content is involved.
          </p>
        </div>
        <button
          type="button"
          className="operator-reload"
          onClick={reload}
          disabled={state.kind === "loading"}
        >
          {state.kind === "loading" ? "Loading…" : "Reload"}
        </button>
      </div>
      {state.kind === "error" && (
        <div className="operator-error">Could not load: {state.message}</div>
      )}
      {state.kind === "loading" && (
        <div className="operator-loading">Loading…</div>
      )}
      {state.kind === "ready" && <MetricsBody m={state.data} />}
    </div>
  );
}

function MetricsBody({ m }: { m: OperatorMetrics }) {
  if (m.summary.total === 0) {
    return <div className="operator-empty">No runs recorded yet.</div>;
  }
  return (
    <div className="operator-metrics">
      <dl className="operator-metrics-summary">
        <div>
          <dt>Total runs</dt>
          <dd>{m.summary.total}</dd>
        </div>
        <div>
          <dt>Completed</dt>
          <dd>{m.summary.completed}</dd>
        </div>
        <div>
          <dt>Off-ramped</dt>
          <dd>{m.summary.off_ramped}</dd>
        </div>
        <div>
          <dt>Median words/msg</dt>
          <dd>{num(m.summary.wpm_median)}</dd>
        </div>
      </dl>

      <section className="operator-metrics-cut">
        <h3>Off-ramps (over-budget estimates)</h3>
        <p className="muted">
          Sessions whose pre-scan estimate exceeded the per-run budget ceiling —
          the user couldn't proceed. Exceedance = estimate ÷ budget.
        </p>
        {m.offramps.length === 0 ? (
          <div className="operator-empty">No off-ramps recorded.</div>
        ) : (
          <table className="operator-table">
            <thead>
              <tr>
                <th>Tier</th>
                <th>Messages</th>
                <th>Estimate</th>
                <th>Budget</th>
                <th>Exceedance</th>
                <th>Words/msg</th>
              </tr>
            </thead>
            <tbody>
              {m.offramps.map((r) => (
                <tr key={r.run_id}>
                  <td>{r.tier ?? "—"}</td>
                  <td>{num(r.message_count)}</td>
                  <td>{usd(r.estimate_usd)}</td>
                  <td>{usd(r.budget_usd)}</td>
                  <td>
                    {r.exceedance_ratio !== null
                      ? `${r.exceedance_ratio.toFixed(2)}×`
                      : "—"}
                  </td>
                  <td>{num(r.words_per_msg_mean)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="operator-metrics-cut">
        <h3>Pricing by corpus size</h3>
        <p className="muted">Completed-run Anthropic cost, bucketed by message count.</p>
        {m.pricing_by_size.length === 0 ? (
          <div className="operator-empty">No completed runs with cost yet.</div>
        ) : (
          <table className="operator-table">
            <thead>
              <tr>
                <th>Size band</th>
                <th>Runs</th>
                <th>Mean</th>
                <th>Min</th>
                <th>Max</th>
              </tr>
            </thead>
            <tbody>
              {m.pricing_by_size.map((r) => (
                <tr key={r.size_band}>
                  <td>{r.size_band.replace(/^\d+\.\s*/, "")}</td>
                  <td>{r.runs}</td>
                  <td>{usd(r.mean_cost)}</td>
                  <td>{usd(r.min_cost)}</td>
                  <td>{usd(r.max_cost)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="operator-metrics-cut">
        <h3>Pricing by configuration</h3>
        <p className="muted">
          Cost grouped by model + prompt version, with a normalized cost per
          1,000 messages for cross-corpus comparison.
        </p>
        {m.pricing_by_config.length === 0 ? (
          <div className="operator-empty">No completed runs with cost yet.</div>
        ) : (
          <table className="operator-table">
            <thead>
              <tr>
                <th>Model</th>
                <th>Prompt</th>
                <th>Runs</th>
                <th>Mean</th>
                <th>Min</th>
                <th>Max</th>
                <th>Cost / 1k msgs</th>
              </tr>
            </thead>
            <tbody>
              {m.pricing_by_config.map((r) => (
                <tr key={`${r.model}|${r.prompt_version}`}>
                  <td><code>{r.model}</code></td>
                  <td><code>{r.prompt_version}</code></td>
                  <td>{r.runs}</td>
                  <td>{usd(r.mean_cost)}</td>
                  <td>{usd(r.min_cost)}</td>
                  <td>{usd(r.max_cost)}</td>
                  <td>{usd(r.cost_per_1k_msgs)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="operator-metrics-cut">
        <h3>Words-per-message anomalies</h3>
        <p className="muted">
          Completed runs flagged when mean words/msg is ≥ 1.5× the cross-run
          median — a "wrote out of the ordinary" signal worth a look.
        </p>
        {m.anomalies.length === 0 ? (
          <div className="operator-empty">No completed runs with word counts yet.</div>
        ) : (
          <table className="operator-table">
            <thead>
              <tr>
                <th>Tier</th>
                <th>Messages</th>
                <th>Mean</th>
                <th>p95</th>
                <th>Max</th>
                <th>Outlier</th>
              </tr>
            </thead>
            <tbody>
              {m.anomalies.map((r) => (
                <tr key={r.run_id} className={r.outlier ? "operator-row-flagged" : undefined}>
                  <td>{r.tier ?? "—"}</td>
                  <td>{num(r.message_count)}</td>
                  <td>{num(r.words_per_msg_mean)}</td>
                  <td>{num(r.words_per_msg_p95)}</td>
                  <td>{num(r.words_per_msg_max)}</td>
                  <td>{r.outlier ? "⚑ yes" : "no"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}


function ReceiptModal({
  row,
  state,
  onClose,
}: {
  row: OperatorRunRecord;
  state: LoadState<OperatorRunRecord>;
  onClose: () => void;
}) {
  // Receipt-endpoint shape == list-row shape today; if/when they
  // diverge the modal will show the receipt-only fields here.
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);
  const modalRef = useRef<HTMLDivElement | null>(null);
  // Stash the element that had focus when the modal opened so we can
  // restore it on close. Without this, keyboard users land on <body>.
  const restoreFocusRef = useRef<HTMLElement | null>(null);

  // Escape closes the modal; Tab/Shift-Tab cycles inside it (focus trap)
  // so keyboard users can't reach the dashboard behind the overlay.
  useEffect(() => {
    restoreFocusRef.current = document.activeElement as HTMLElement | null;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== "Tab" || !modalRef.current) return;
      const focusables = Array.from(
        modalRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, textarea, select, [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => !el.hasAttribute("disabled") && el.tabIndex !== -1);
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      restoreFocusRef.current?.focus();
    };
  }, [onClose]);

  // Autofocus the Close button so keyboard users can dismiss with Enter.
  useEffect(() => {
    closeBtnRef.current?.focus();
  }, []);

  return (
    <div
      className="operator-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="receipt-modal-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div ref={modalRef} className="operator-modal">
        <div className="operator-modal-head">
          <h2 id="receipt-modal-title">Run receipt</h2>
          <button ref={closeBtnRef} type="button" onClick={onClose}>Close</button>
        </div>
        <dl className="operator-modal-list">
          <dt>Cloud run ID</dt>
          <dd><code>{row.cloud_run_id}</code></dd>
          <dt>Status</dt>
          <dd>{state.kind === "ready" ? state.data.status : row.status}</dd>
          <dt>Started</dt>
          <dd>{formatLocalTime(row.started_at)}</dd>
          <dt>Completed</dt>
          <dd>
            {state.kind === "ready"
              ? formatLocalTime(state.data.completed_at)
              : formatLocalTime(row.completed_at)}
          </dd>
          <dt>Cost (USD)</dt>
          <dd>
            {state.kind === "loading"
              ? "Loading…"
              : state.kind === "ready" && state.data.cost_usd_anthropic !== null
                ? `$${state.data.cost_usd_anthropic.toFixed(2)}`
                : row.cost_usd_anthropic !== null
                  ? `$${row.cost_usd_anthropic.toFixed(2)}`
                  : "—"}
          </dd>
          <dt>Access code</dt>
          <dd>
            {row.access_code
              ? <code>{row.access_code}</code>
              : "—"}
          </dd>
          <dt>Owner</dt>
          <dd><OwnerCell name={row.owner_name} email={row.owner_email ?? row.notify_email} /></dd>
          {state.kind === "error" && (
            <>
              <dt>Receipt fetch error</dt>
              <dd className="operator-modal-error">{state.message}</dd>
            </>
          )}
        </dl>
      </div>
    </div>
  );
}


// One unified feedback entry — a post-run rating or an in-app note —
// normalized so the panel renders and sorts them in a single stream.
type FeedbackEntry = {
  key: string;
  when: string;
  score: number | null;
  message: string | null;
  from: string | null;
  reviewed: boolean;
  kind: "rating" | "note";
  feedbackId?: string;
  cloudRunId?: string;
};

function buildFeedbackEntries(
  feedback: OperatorFeedback[],
  ratings: OperatorFeedbackRating[],
): FeedbackEntry[] {
  const entries: FeedbackEntry[] = [
    ...ratings.map((r) => ({
      key: `r:${r.cloud_run_id}`,
      when: r.updated_at,
      score: r.score,
      message: r.comment,
      from: r.access_code,
      reviewed: r.reviewed,
      kind: "rating" as const,
      cloudRunId: r.cloud_run_id,
    })),
    ...feedback.map((f) => ({
      key: `f:${f.id}`,
      when: f.submitted_at,
      score: null,
      message: f.body,
      from: f.email ?? f.access_code,
      // "new" = not yet reviewed; any other status counts as reviewed.
      reviewed: f.status !== "new",
      kind: "note" as const,
      feedbackId: f.id,
    })),
  ];
  entries.sort((a, b) => new Date(b.when).getTime() - new Date(a.when).getTime());
  return entries;
}

function Stars({ score }: { score: number }) {
  return (
    <span className="fb-stars" aria-label={`${score} of 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <span key={n} className={n <= score ? "" : "off"}>
          ★
        </span>
      ))}
    </span>
  );
}

// Unified Feedback panel: ratings (from the post-run email) and notes (from
// the in-app button) in one reverse-chron stream, with a summary + 1–5
// distribution and a reversible per-entry "reviewed" toggle. Nothing is ever
// removed — reviewed entries dim, and "Hide reviewed" filters them out.
function FeedbackPanel({ server, token }: { server: string; token: string }) {
  const loader = useCallback(
    () => operatorListFeedback(server, token),
    [server, token],
  );
  const { state, reload } = useLoader<{
    feedback: OperatorFeedback[];
    ratings: OperatorFeedbackRating[];
  }>(loader);
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [hideReviewed, setHideReviewed] = useState(false);

  const feedback = state.kind === "ready" ? state.data.feedback ?? [] : [];
  const ratings = state.kind === "ready" ? state.data.ratings ?? [] : [];
  const entries = buildFeedbackEntries(feedback, ratings);
  const visible = hideReviewed ? entries.filter((e) => !e.reviewed) : entries;

  const scores = ratings.map((r) => r.score);
  const avg = scores.length
    ? scores.reduce((a, b) => a + b, 0) / scores.length
    : null;
  const dist = [1, 2, 3, 4, 5].map((n) => scores.filter((s) => s === n).length);
  const maxDist = Math.max(1, ...dist);

  async function toggleReviewed(entry: FeedbackEntry) {
    if (busy === entry.key) return;
    setBusy(entry.key);
    setActionError(null);
    try {
      if (entry.kind === "rating" && entry.cloudRunId) {
        await operatorSetRatingReviewed(
          server, token, entry.cloudRunId, !entry.reviewed,
        );
      } else if (entry.kind === "note" && entry.feedbackId) {
        await operatorUpdateFeedbackStatus(
          server, token, entry.feedbackId, entry.reviewed ? "new" : "reviewed",
        );
      }
      reload();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="operator-panel">
      <div className="operator-panel-head">
        <div>
          <h2>Feedback</h2>
          <p className="muted">
            Ratings from the post-run email and notes from the in-app button,
            newest first.
          </p>
        </div>
        <button
          type="button"
          className="operator-reload"
          onClick={reload}
          disabled={state.kind === "loading"}
        >
          {state.kind === "loading" ? "Loading…" : "Reload"}
        </button>
      </div>

      {actionError && (
        <div className="operator-error">Action failed: {actionError}</div>
      )}
      {state.kind === "error" && (
        <div className="operator-error">Could not load: {state.message}</div>
      )}
      {state.kind === "loading" && (
        <div className="operator-loading">Loading…</div>
      )}
      {state.kind === "ready" && entries.length === 0 && (
        <div className="operator-empty">No feedback yet.</div>
      )}

      {state.kind === "ready" && entries.length > 0 && (
        <>
          <div className="fb-summary">
            <div className="fb-avg">
              {avg !== null ? (
                <>
                  <b>{avg.toFixed(1)}</b>
                  <Stars score={Math.round(avg)} />
                  <span className="muted">
                    · {scores.length} rating{scores.length === 1 ? "" : "s"} ·{" "}
                    {feedback.length} note{feedback.length === 1 ? "" : "s"}
                  </span>
                </>
              ) : (
                <span className="muted">
                  {feedback.length} note{feedback.length === 1 ? "" : "s"} · no
                  ratings yet
                </span>
              )}
            </div>
            {avg !== null && (
              <div className="fb-dist">
                {[5, 4, 3, 2, 1].map((n) => (
                  <div className="fb-distrow" key={n}>
                    <i>{n}</i>
                    <div className="fb-bar">
                      <span
                        style={{ width: `${(dist[n - 1] / maxDist) * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
            <label className="fb-filter">
              <input
                type="checkbox"
                checked={hideReviewed}
                onChange={(e) => setHideReviewed(e.target.checked)}
              />
              Hide reviewed
            </label>
          </div>

          <div className="fb-list">
            {visible.map((entry) => (
              <div
                key={entry.key}
                className={`fb-item${entry.reviewed ? " reviewed" : ""}`}
              >
                <div className="fb-rating">
                  {entry.kind === "rating" && entry.score != null ? (
                    <>
                      <Stars score={entry.score} />
                      <span className="num">{entry.score} / 5</span>
                    </>
                  ) : (
                    <span className="fb-note-tag">Note</span>
                  )}
                </div>
                <div>
                  <div className={`fb-msg${entry.message ? "" : " empty"}`}>
                    {entry.message ?? "No note left."}
                  </div>
                  <div className="fb-meta">
                    {entry.from ? <code>{entry.from}</code> : "—"} ·{" "}
                    {formatLocalTime(entry.when)}
                  </div>
                </div>
                <button
                  type="button"
                  className={`fb-rev${entry.reviewed ? " on" : ""}`}
                  onClick={() => toggleReviewed(entry)}
                  disabled={busy === entry.key}
                >
                  {entry.reviewed ? "✓ Reviewed" : "Mark reviewed"}
                </button>
              </div>
            ))}
            {visible.length === 0 && (
              <div className="operator-empty">All reviewed.</div>
            )}
          </div>
        </>
      )}
    </div>
  );
}


// Label vocabulary mirrors the run screen's chips (RunState HOW_HEARD_CHIPS).
// An unmapped slug degrades to its raw value rather than hiding the row, so
// a new chip shipped client-side shows up here before this map learns it.
export const HOW_HEARD_LABELS: Record<string, string> = {
  facebook: "Facebook",
  "private-group": "Private group",
  linkedin: "LinkedIn",
  "web-search": "Web search",
  "ai-chat": "AI chat",
  "talk-podcast": "Talk or podcast",
  referral: "Referral",
  other: "Other",
};

export function howHeardLabel(source: string | null): string {
  if (!source) return "Unrecorded";
  return HOW_HEARD_LABELS[source] ?? source;
}

export function HowHeardPanel({ server, token }: { server: string; token: string }) {
  const loader = useCallback(
    () => operatorListHowHeard(server, token),
    [server, token],
  );
  const { state, reload } = useLoader<{ answers: OperatorHowHeard[] }>(loader);
  const answers = state.kind === "ready" ? state.data.answers ?? [] : [];

  const [addSource, setAddSource] = useState("");
  const [addName, setAddName] = useState("");
  const [addDetail, setAddDetail] = useState("");
  const [addCode, setAddCode] = useState("");
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  async function submitAdd(e: FormEvent) {
    e.preventDefault();
    if (!addSource || adding) return;
    setAdding(true);
    setAddError(null);
    try {
      await operatorAddHowHeard(server, token, {
        source: addSource,
        name: addName.trim() || null,
        detail: addDetail.trim() || null,
        access_code: addCode.trim() || null,
      });
      setAddSource("");
      setAddName("");
      setAddDetail("");
      setAddCode("");
      reload();
    } catch (err) {
      setAddError(err instanceof Error ? err.message : "Could not log referral.");
    } finally {
      setAdding(false);
    }
  }

  const counts = new Map<string, number>();
  for (const a of answers) {
    const label = howHeardLabel(a.source);
    counts.set(label, (counts.get(label) ?? 0) + 1);
  }
  const dist = [...counts.entries()].sort((x, y) => y[1] - x[1]);
  const maxCount = Math.max(1, ...dist.map(([, n]) => n));

  return (
    <div className="operator-panel">
      <div className="operator-panel-head">
        <div>
          <h2>How they heard</h2>
          <p className="muted">
            Answers to the optional question on the run screen, newest first.
            Skips are never sent, so this only counts people who tapped a
            chip. The apply form&rsquo;s own answer lives on each application.
          </p>
        </div>
        <button
          type="button"
          className="operator-reload"
          onClick={reload}
          disabled={state.kind === "loading"}
        >
          {state.kind === "loading" ? "Loading…" : "Reload"}
        </button>
      </div>

      <form className="hhp-add" onSubmit={submitAdd}>
        <span className="hhp-add-label">Log a referral</span>
        <select
          value={addSource}
          onChange={(e) => setAddSource(e.target.value)}
          aria-label="Referral source"
          required
        >
          <option value="">Choose a source…</option>
          {Object.entries(HOW_HEARD_LABELS).map(([slug, label]) => (
            <option key={slug} value={slug}>{label}</option>
          ))}
        </select>
        <input
          type="text"
          value={addName}
          onChange={(e) => setAddName(e.target.value)}
          placeholder="Name (e.g. Alicia Urban)"
          maxLength={120}
          aria-label="Referral name"
        />
        <input
          type="text"
          value={addDetail}
          onChange={(e) => setAddDetail(e.target.value)}
          placeholder="Detail (e.g. single-moms FB group)"
          maxLength={200}
          aria-label="Referral detail"
        />
        <input
          type="text"
          value={addCode}
          onChange={(e) => setAddCode(e.target.value)}
          placeholder="Access code (optional)"
          maxLength={64}
          aria-label="Access code"
        />
        <button type="submit" disabled={!addSource || adding}>
          {adding ? "Adding…" : "Add"}
        </button>
      </form>
      {addError && <div className="operator-error">{addError}</div>}

      {state.kind === "error" && (
        <div className="operator-error">Could not load: {state.message}</div>
      )}
      {state.kind === "loading" && (
        <div className="operator-loading">Loading…</div>
      )}
      {state.kind === "ready" && answers.length === 0 && (
        <div className="operator-empty">No answers yet.</div>
      )}

      {state.kind === "ready" && answers.length > 0 && (
        <>
          <div className="fb-summary">
            <div className="fb-avg">
              <b>{answers.length}</b>
              <span className="muted">
                answer{answers.length === 1 ? "" : "s"}
              </span>
            </div>
            <div className="hhp-dist">
              {dist.map(([label, n]) => (
                <div className="hhp-distrow" key={label}>
                  <i>{label}</i>
                  <div className="fb-bar">
                    <span style={{ width: `${(n / maxCount) * 100}%` }} />
                  </div>
                  <b>{n}</b>
                </div>
              ))}
            </div>
          </div>

          <div className="fb-list">
            {answers.map((a, i) => (
              <div className="fb-item" key={`${a.recorded_at}-${i}`}>
                <div className="fb-rating">
                  <span className="fb-note-tag">{howHeardLabel(a.source)}</span>
                  {a.entered_by === "operator" && (
                    <span className="hhp-logged">logged</span>
                  )}
                </div>
                <div>
                  {a.name && <div className="hhp-name">{a.name}</div>}
                  <div className={`fb-msg${a.detail ? "" : " empty"}`}>
                    {a.detail ?? "No detail added."}
                  </div>
                  <div className="fb-meta">
                    {a.access_code ? <code>{a.access_code}</code> : "—"} ·{" "}
                    {formatLocalTime(a.recorded_at)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}


// --- Settings panel: a single run-mode picker + the pilot key ---


function StatePill({
  tone,
  children,
}: {
  tone: "ok" | "warn" | "info" | "danger";
  children: React.ReactNode;
}) {
  return <span className={`operator-pill operator-pill-${tone}`}>{children}</span>;
}


type RunMode = "demo" | "pilot" | "live";

const RUN_MODES: { id: RunMode; label: string; desc: string }[] = [
  { id: "demo", label: "Demo", desc: "Nothing real runs · $0 · nothing sent to Anthropic" },
  { id: "pilot", label: "Pilot", desc: "Real analyses · paid by the grant key · users see “free”" },
  { id: "live", label: "Live (paid)", desc: "Real analyses · paid by YOUR card · users see normal prices" },
];

function currentMode(s: OperatorSettings): RunMode {
  if (s.use_fake_provider.value) return "demo";
  return s.pilot_free.value ? "pilot" : "live";
}

// Plain-English "what's true right now" summary for the top of the Run-mode
// panel — composed from the derived billing fields describe() already ships
// (active_source / active_key_set / billable_runs_blocked) so the operator
// reads "who's charged / can a run launch" instead of reconstructing it from
// the raw switches. The invisible failure state — Pilot/Live with no key, runs
// blocked — surfaces here rather than only as a user-facing 503.
function describeRunPosture(s: OperatorSettings): { text: string; blocked: boolean } {
  if (currentMode(s) === "demo") {
    return {
      text: "Demo — nothing real runs, $0, nothing sent to Anthropic.",
      blocked: false,
    };
  }
  const b = s.billing;
  const label = b.active_source === "pilot" ? "Pilot" : "Live (paid)";
  const who = b.active_source === "pilot" ? "the grant key" : "your card key";
  if (b.billable_runs_blocked) {
    // Pilot has an editable key input below; Live's card key is env-only
    // (ANTHROPIC_API_KEY), so don't tell the operator to "add a card key below"
    // — there is no such control in the UI.
    const fix =
      b.active_source === "pilot"
        ? "Add a pilot key below."
        : "Set ANTHROPIC_API_KEY on the server.";
    return {
      text: `${label} — real analyses would charge ${who}, but it isn't set, so runs are blocked. ${fix}`,
      blocked: true,
    };
  }
  return {
    text: `${label} — real analyses, charged to ${who} (loaded). Runs can launch.`,
    blocked: false,
  };
}

const FANOUT_MODE_LABELS: Record<OperatorFanoutMode, { label: string; desc: string }> = {
  auto: { label: "Auto", desc: "Size-based · small → sync, large → batch" },
  batch: { label: "Batch", desc: "Force Batches API · cheaper, slower to start" },
  sync: { label: "Sync", desc: "Force inline · faster, pricier" },
};

// Shared radiogroup for the operator's segmented choices (run mode, fan-out
// transport). idColorClass adds the per-id `operator-mode-${id}` accent the run
// mode selector uses (demo/pilot/live colors); fan-out leaves it off.
function OperatorModeRadio({
  ariaLabel,
  options,
  selectedId,
  onSelect,
  busy,
  idColorClass = false,
}: {
  ariaLabel: string;
  options: { id: string; label: string; desc: string }[];
  selectedId: string;
  onSelect: (id: string) => void;
  busy: boolean;
  idColorClass?: boolean;
}) {
  return (
    <div className="operator-modes" role="radiogroup" aria-label={ariaLabel}>
      {options.map((m) => {
        const selected = selectedId === m.id;
        return (
          <button
            key={m.id}
            type="button"
            role="radio"
            aria-checked={selected}
            className={`operator-mode${idColorClass ? ` operator-mode-${m.id}` : ""}${selected ? " selected" : ""}`}
            onClick={() => onSelect(m.id)}
            disabled={busy}
          >
            <span className={`operator-mode-dot${selected ? " on" : ""}`} aria-hidden="true" />
            <span className="operator-mode-text">
              <span className="operator-mode-label">{m.label}</span>
              <span className="operator-mode-desc">{m.desc}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}


type SettingsSection = "runmode" | "limits" | "pricing";

const SETTINGS_SECTION_META: Record<
  SettingsSection,
  { title: string; subtitle: string }
> = {
  runmode: {
    title: "Run mode & keys",
    subtitle:
      "How analyses run and who pays, the pilot key, and the outage banner. Takes effect on the next run.",
  },
  limits: {
    title: "Limits",
    subtitle:
      "Safety caps on spend, file size, and traffic. Changes apply to the next request — no restart. Revert returns a value to the deploy default.",
  },
  pricing: {
    title: "Pricing",
    subtitle: "Per-tier prices shown on the cost screen. Edit without a redeploy.",
  },
};

function SettingsPanel({
  server,
  token,
  section,
}: {
  server: string;
  token: string;
  section: SettingsSection;
}) {
  const loader = useCallback(
    () => operatorGetSettings(server, token),
    [server, token],
  );
  const { state, reload } = useLoader<OperatorSettings>(loader);
  // Mutations return the full snapshot — hold it locally so the panel
  // reflects a change instantly without a refetch.
  const [live, setLive] = useState<OperatorSettings | null>(null);
  const settings = live ?? (state.kind === "ready" ? state.data : null);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Live (paid) bills the personal card — guard that one choice.
  const [confirmLive, setConfirmLive] = useState(false);
  const [keyInput, setKeyInput] = useState("");

  async function guarded(fn: () => Promise<OperatorSettings>) {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      setLive(await fn());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function applyMode(mode: RunMode) {
    // One atomic server call — sets both flags and logs a single
    // legible audit row (set_run_mode → <mode>).
    await guarded(() => operatorSetMode(server, token, mode));
    setConfirmLive(false);
  }

  function selectMode(mode: RunMode, active: RunMode) {
    if (mode === active) return;
    if (mode === "live") {
      setConfirmLive(true); // confirm the personal-card mode
      return;
    }
    setConfirmLive(false);
    applyMode(mode);
  }

  if (!settings) {
    return (
      <div className="operator-panel">
        {state.kind === "error" ? (
          <div className="operator-error">Could not load settings: {state.message}</div>
        ) : (
          <div className="operator-loading">Loading…</div>
        )}
      </div>
    );
  }

  const mode = currentMode(settings);
  const keySet = settings.pilot_key.set;
  const posture = describeRunPosture(settings);
  const personalKey = settings.personal_key;
  const meta = SETTINGS_SECTION_META[section];

  return (
    <div className="operator-panel operator-settings">
      {/* Pricing self-heads via PricingSection (its own title + reload); the
          other sections get the generic panel head. */}
      {section !== "pricing" && (
        <div className="operator-panel-head">
          <div>
            <h2>{meta.title}</h2>
            <p className="muted">{meta.subtitle}</p>
          </div>
          <button
            type="button"
            className="operator-reload"
            onClick={() => {
              setLive(null);
              reload();
            }}
            disabled={state.kind === "loading"}
          >
            {state.kind === "loading" ? "Loading…" : "Reload"}
          </button>
        </div>
      )}

      {error && <div className="operator-error">Action failed: {error}</div>}

      {section === "runmode" && (
        <>
      <OperatorModeRadio
        ariaLabel="Run mode"
        options={RUN_MODES}
        selectedId={mode}
        onSelect={(id) => selectMode(id as RunMode, mode)}
        busy={busy}
        idColorClass
      />

      {/* The one-sentence "what's true right now" summary — who's charged, can a
          run launch — so the derived truth isn't reconstructed from the radio. */}
      <p
        className={
          posture.blocked
            ? "operator-posture operator-error operator-inline-warn"
            : "operator-posture muted"
        }
      >
        <strong>Right now:</strong> {posture.text}
      </p>

      {confirmLive && (
        <div className="operator-confirm">
          <p className="muted">
            <strong>Live (paid)</strong> bills YOUR card on every real analysis. During the pilot
            you almost always want <strong>Pilot</strong> — real analyses, paid by the grant key,
            free to users.
          </p>
          <div className="operator-confirm-actions">
            <button
              type="button"
              className="operator-row-action operator-danger"
              onClick={() => applyMode("live")}
              disabled={busy}
            >
              Switch to Live (paid)
            </button>
            <button
              type="button"
              className="operator-row-action"
              onClick={() => setConfirmLive(false)}
              disabled={busy}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="operator-key-row">
        <span className="operator-key-label">Pilot key</span>
        {keySet ? (
          <StatePill tone="ok">Set · {settings.pilot_key.masked}</StatePill>
        ) : (
          <StatePill tone="warn">Not set</StatePill>
        )}
        <input
          type="password"
          className="operator-input"
          value={keyInput}
          onChange={(e) => setKeyInput(e.target.value)}
          placeholder={keySet ? "replace…" : "sk-ant-…"}
          aria-label="Pilot Anthropic key"
          autoComplete="off"
        />
        <button
          type="button"
          className="operator-row-action"
          disabled={!keyInput.trim() || busy}
          onClick={async () => {
            await guarded(() => operatorSetPilotKey(server, token, keyInput.trim()));
            setKeyInput("");
          }}
        >
          {keySet ? "Replace" : "Save"}
        </button>
        {keySet && (
          <button
            type="button"
            className="operator-row-action operator-danger"
            disabled={busy}
            onClick={() => guarded(() => operatorClearPilotKey(server, token))}
          >
            Clear
          </button>
        )}
      </div>

      {mode === "pilot" && !keySet && (
        <p className="operator-error operator-inline-warn">
          Pilot mode needs a pilot key — runs are blocked until you add one.
        </p>
      )}

      {/* The card key Live mode charges — read-only (env ANTHROPIC_API_KEY),
          surfaced so the operator can confirm which key is loaded. */}
      {mode === "live" && (
        <div className="operator-key-row">
          <span className="operator-key-label">Card key</span>
          {personalKey.set ? (
            <StatePill tone="ok">Set · {personalKey.masked}</StatePill>
          ) : (
            <StatePill tone="warn">Not set</StatePill>
          )}
          <span className="muted">
            Live charges this key · set via the ANTHROPIC_API_KEY env, not here.
          </span>
        </div>
      )}

      {"public_access" in settings && (
        <div className="operator-outage">
          <h3 className="operator-limits-head">Self-serve checkout</h3>
          {settings.billing.stripe_configured ? (
            <>
              <FlagToggleRow
                label="Public self-serve checkout"
                desc="When ON, the public can pay for a case via Stripe. Flip OFF to pull back to invite-only instantly — this stops NEW checkouts only; in-flight payments still complete and already-issued codes keep working."
                state={settings.public_access}
                busy={busy}
                dangerWhenOn
                onToggle={(v) => {
                  if (
                    v &&
                    !window.confirm(
                      "Open public self-serve checkout? The public will be able to pay for a case with a card. (No effect on a fake-provider box.)",
                    )
                  )
                    return;
                  guarded(() => operatorSetFlag(server, token, "public_access", v));
                }}
              />
              <p
                className={
                  settings.public_access.value && !settings.billing.self_serve_open
                    ? "operator-error operator-inline-warn"
                    : "muted"
                }
              >
                {settings.billing.self_serve_open
                  ? "Checkout is OPEN — the public can buy a case right now."
                  : settings.public_access.value
                    ? "Switch is ON, but checkout stays CLOSED — the provider is in fake mode (Demo), so no card is ever charged."
                    : "Checkout is CLOSED — invite-only (operator-minted codes)."}
              </p>
            </>
          ) : (
            // Stripe isn't wired (enabled + webhook secret) — there's nothing to
            // gate yet, so we DON'T show a live toggle that could only sit in a
            // confusing "ON but closed" state. The toggle appears (as the live
            // pull-back) once Stripe is configured.
            <p className="muted">
              Public checkout is <strong>not available yet</strong> — Stripe isn&rsquo;t
              configured on this server. Access stays invite-only (operator-minted
              codes). The switch to open or pull back public checkout appears here
              once Stripe is wired.
              {settings.public_access.value && (
                // A public_access=ON override set while Stripe was configured is
                // inert now (checkout stays closed, fail-closed) but would be
                // stranded — the toggle is hidden — so offer a way to clear it.
                <>
                  {" "}A stale &ldquo;open&rdquo; setting is still saved (inert while
                  Stripe is off) —{" "}
                  <button
                    type="button"
                    className="operator-switch-reset"
                    disabled={busy}
                    onClick={() =>
                      guarded(() => operatorSetFlag(server, token, "public_access", null))
                    }
                  >
                    clear it
                  </button>
                  .
                </>
              )}
            </p>
          )}
        </div>
      )}

      {"anthropic_service_degraded" in settings && (
        <div className="operator-outage">
          <h3 className="operator-limits-head">Service status</h3>
          <FlagToggleRow
            label="Anthropic outage banner"
            desc="Shows a site-wide “slower than usual today” banner and forces the outage line into keep-informed emails. It turns on automatically when a degraded provider is detected — flip this to show it preemptively or keep it up."
            state={settings.anthropic_service_degraded}
            busy={busy}
            dangerWhenOn
            onToggle={(v) => guarded(() => operatorSetFlag(server, token, "anthropic_service_degraded", v))}
          />
        </div>
      )}

      {"fanout_mode" in settings && settings.fanout_mode.force_allowed && (
        <div className="operator-fanout">
          <h3 className="operator-limits-head">Fan-out transport <span className="operator-fanout-staging">staging only</span></h3>
          <p className="muted operator-fanout-note">
            How real runs fan out across patterns.{" "}
            <strong>Auto</strong> routes a small corpus (under{" "}
            {settings.fanout_mode.auto_sync_max_tokens.toLocaleString()} reply tokens)
            to sync — fast, no Batches-API queue wait — and a larger one to batch for
            the cost saving. <strong>Batch</strong>/<strong>Sync</strong> force the
            transport, e.g. to smoke-test a small corpus on either path. Demo mode is
            always sync.
          </p>
          <OperatorModeRadio
            ariaLabel="Fan-out transport"
            options={settings.fanout_mode.options.map((m) => ({
              id: m,
              label: FANOUT_MODE_LABELS[m].label,
              desc: FANOUT_MODE_LABELS[m].desc,
            }))}
            selectedId={settings.fanout_mode.value}
            onSelect={(id) => {
              if (id !== settings.fanout_mode.value) {
                guarded(() => operatorSetEnum(server, token, "fanout_mode", id));
              }
            }}
            busy={busy}
          />
          {settings.fanout_mode.source === "override" && (
            <button
              type="button"
              className="operator-row-action operator-fanout-revert"
              disabled={busy}
              onClick={() => guarded(() => operatorSetEnum(server, token, "fanout_mode", null))}
            >
              Revert to default ({settings.fanout_mode.env_default})
            </button>
          )}
        </div>
      )}
        </>
      )}

      {section === "limits" && (
      <div className="operator-limits">
        {/* key forces a re-init of the input from the authoritative value
            after a save/revert (not while typing — state only changes on save).
            Rows are grouped by what they protect (owner ask, 2026-08-05):
            one analysis's cost, the whole day's spend, and the door guards —
            with a plain-words line under every label. */}
        <h3 className="operator-limits-head">What one analysis may cost</h3>
        <LimitRow
          key={`budget-${settings.limits.anthropic_budget_usd.source}-${settings.limits.anthropic_budget_usd.value}`}
          label="Per-run budget ceiling ($)"
          desc="The most one analysis is allowed to cost. A file estimated above this can't be bought at any price, and the analysis won't start."
          unit="$"
          scale={1}
          step="1"
          state={settings.limits.anthropic_budget_usd}
          busy={busy}
          onSave={(v) => guarded(() => operatorSetLimit(server, token, "anthropic_budget_usd", v))}
        />
        {/* Door 2 (2026-08-05): the Access-tier cost ceiling. The row above is
            the STANDARD ceiling (Standard + Solidarity cards + the run-start
            gate); this one only marks the Access card viable/inviable on the
            cost screen. Older servers omit it from /operator/settings —
            render the row only when present so the panel stays deploy-order
            tolerant. */}
        {settings.limits.anthropic_budget_usd_access && (
          <LimitRow
            key={`budget-access-${settings.limits.anthropic_budget_usd_access.source}-${settings.limits.anthropic_budget_usd_access.value}`}
            label="Access-tier budget ceiling ($)"
            desc="Same ceiling, tighter, for the $199 Access choice only — keeps a $199 purchase from funding a costlier analysis. Above it, the Access card grays out. Never rises above the per-run ceiling."
            unit="$"
            scale={1}
            step="1"
            state={settings.limits.anthropic_budget_usd_access}
            busy={busy}
            onSave={(v) => guarded(() => operatorSetLimit(server, token, "anthropic_budget_usd_access", v))}
          />
        )}
        <h3 className="operator-limits-head">What a day may cost</h3>
        <LimitRow
          key={`daily-preview-${settings.limits.daily_preview_cap_usd.source}-${settings.limits.daily_preview_cap_usd.value}`}
          label="Daily preview cap ($)"
          desc="Spending stop for the whole service: once today's free previews together cost this much, previews pause until tomorrow."
          unit="$"
          scale={1}
          step="1"
          state={settings.limits.daily_preview_cap_usd}
          busy={busy}
          onSave={(v) => guarded(() => operatorSetLimit(server, token, "daily_preview_cap_usd", v))}
        />
        <LimitRow
          key={`daily-conversion-${settings.limits.daily_conversion_cap_usd.source}-${settings.limits.daily_conversion_cap_usd.value}`}
          label="Daily conversion cap ($)"
          desc="The same daily stop for full paid analyses — the ceiling on what even a very good day can cost."
          unit="$"
          scale={1}
          step="1"
          state={settings.limits.daily_conversion_cap_usd}
          busy={busy}
          onSave={(v) => guarded(() => operatorSetLimit(server, token, "daily_conversion_cap_usd", v))}
        />
        <LimitRow
          key={`daily-classify-${settings.limits.daily_classify_cap_usd.source}-${settings.limits.daily_classify_cap_usd.value}`}
          label="Daily classify cap ($)"
          desc="The daily stop for the small who's-involved readings Aurora does when a file is uploaded. Each one is tiny; this caps a flood of them."
          unit="$"
          scale={1}
          step="1"
          state={settings.limits.daily_classify_cap_usd}
          busy={busy}
          onSave={(v) => guarded(() => operatorSetLimit(server, token, "daily_classify_cap_usd", v))}
        />
        <h3 className="operator-limits-head">Size and traffic guards</h3>
        <LimitRow
          key={`upload-${settings.limits.max_upload_bytes.source}-${settings.limits.max_upload_bytes.value}`}
          label="Upload cap (MB)"
          desc="The largest file the server will accept, on every door a file comes in through."
          unit=" MB"
          scale={1024 * 1024}
          step="1"
          state={settings.limits.max_upload_bytes}
          busy={busy}
          onSave={(v) => guarded(() => operatorSetLimit(server, token, "max_upload_bytes", v))}
        />
        <LimitRow
          key={`rate-${settings.limits.prescan_rate_limit_per_hour.source}-${settings.limits.prescan_rate_limit_per_hour.value}`}
          label="Prescan rate (per hour)"
          desc="How many size-and-cost readings one visitor can request in an hour. Slows down anyone hammering the free check; paying customers are unaffected."
          unit="/hr"
          scale={1}
          step="1"
          state={settings.limits.prescan_rate_limit_per_hour}
          busy={busy}
          onSave={(v) => guarded(() => operatorSetLimit(server, token, "prescan_rate_limit_per_hour", v))}
        />
      </div>
      )}

      {section === "pricing" && <PricingSection server={server} token={token} />}
    </div>
  );
}

// True when an operator-triggered email would NOT actually be delivered —
// either fake-email mode is on or the server has no Resend key. The single
// source of truth used by every "send" surface (Email panel banner, Applications
// approve guard) so they can't disagree about whether mail goes out.
function emailBlocked(email: OperatorEmailSettings): boolean {
  return email.use_fake_email.value || !email.resend_key_set;
}

// Standalone operator panel for everything email: from/reply addresses, the
// fake-email + feedback toggles, the feedback delay, the three editable
// transactional templates (subject + body, with click-to-insert placeholder
// chips + reset), and a test-send. Loads its own settings snapshot, like
// SettingsPanel does.
function EmailPanel({
  server,
  token,
  section,
}: {
  server: string;
  token: string;
  section: "config" | "templates";
}) {
  const loader = useCallback(
    () => operatorGetSettings(server, token),
    [server, token],
  );
  const { state } = useLoader<OperatorSettings>(loader);
  // Mutations return the full snapshot — hold it locally so the panel
  // reflects a change instantly without a refetch.
  const [live, setLive] = useState<OperatorSettings | null>(null);
  const settings = live ?? (state.kind === "ready" ? state.data : null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function apply(fn: () => Promise<OperatorSettings>) {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      setLive(await fn());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  if (!settings) {
    return (
      <div className="operator-panel">
        {state.kind === "error" ? (
          <div className="operator-error">Could not load email settings: {state.message}</div>
        ) : (
          <div className="operator-loading">Loading…</div>
        )}
      </div>
    );
  }

  const email = settings.email;
  return (
    <div className="operator-panel operator-email">
      <div className="operator-panel-head">
        <div>
          <h2>{section === "templates" ? "Email templates" : "Email"}</h2>
          {section === "templates" ? (
            <p className="muted">
              The wording of every email Aurora sends, in the order a pilot user
              meets them.
            </p>
          ) : (
            <p className="muted">
              Who transactional emails come from, and the sending toggles. The
              Resend sending key stays in the server&rsquo;s environment —
              it&rsquo;s {email.resend_key_set ? "set" : "not set"} there.
            </p>
          )}
        </div>
      </div>

      {error && <div className="operator-error operator-inline-warn">{error}</div>}

      {emailBlocked(email) && (
        <div className="operator-email-blocked" role="status">
          <strong>
            {email.use_fake_email.value
              ? "Fake email is ON — nothing is actually delivered."
              : "No Resend key on this server — nothing is actually delivered."}
          </strong>
          <span>
            {email.use_fake_email.value
              ? "Approvals, come-back links, and feedback notes are logged, not sent. Turn it off below to send for real."
              : "Set RESEND_API_KEY in the server environment to send for real."}
          </span>
        </div>
      )}

      {section === "config" && (
        <>
      <h3 className="operator-email-subhead">Addresses</h3>
      <AddressRow
        key={`from-${email.from_address.source}-${email.from_address.value ?? ""}`}
        label="From address"
        name="email_from_address"
        state={email.from_address}
        busy={busy}
        apply={apply}
        server={server}
        token={token}
        placeholder="Aurora <hello@mail.plaintosee.cc>"
        hint="Must be a verified Resend sender."
      />
      <AddressRow
        key={`reply-${email.reply_to_address.source}-${email.reply_to_address.value ?? ""}`}
        label="Reply-to address"
        name="email_reply_to_address"
        state={email.reply_to_address}
        busy={busy}
        apply={apply}
        server={server}
        token={token}
        placeholder="you@plaintosee.cc"
        hint="Where replies (incl. feedback) land — use an inbox you watch."
      />

      <h3 className="operator-email-subhead">Sending</h3>
      {/* Guard: a server deployed before the ready-email flag existed won't
          return it — render the row only when present (don't crash the tab). */}
      {"ready_email_enabled" in email && (
        <FlagToggleRow
          label="Send “analysis ready” emails"
          desc="As soon as a run finishes, email the person a link back to it — so they don’t have to watch a long run."
          state={email.ready_email_enabled}
          busy={busy}
          onToggle={(v) => apply(() => operatorSetFlag(server, token, "ready_email_enabled", v))}
        />
      )}
      <FlagToggleRow
        label="Send feedback emails"
        desc="A one-time “how did it go?” note a few hours after each finished run."
        state={email.feedback_email_enabled}
        busy={busy}
        onToggle={(v) => apply(() => operatorSetFlag(server, token, "feedback_email_enabled", v))}
      />
      <FlagToggleRow
        label="Fake email (don’t actually send)"
        desc="On = emails are logged, not delivered. Useful for testing. Independent of the Demo run mode — you can preview email copy without sending, or send real mail during a real run."
        state={email.use_fake_email}
        busy={busy}
        dangerWhenOn
        onToggle={(v) => {
          // Leaving fake mail = the next finished run sends REAL email on
          // whatever keys this box holds. The accidental-flip incident
          // (2026-07-02) rode exactly this switch — make it deliberate.
          if (
            !v &&
            !window.confirm(
              "Turn OFF fake email? Emails will actually be delivered — on a box with real mail keys this sends real messages to real addresses.",
            )
          )
            return;
          apply(() => operatorSetFlag(server, token, "use_fake_email", v));
        }}
      />

      <LimitRow
        key={`fbdelay-${email.feedback_email_delay_hours.source}-${email.feedback_email_delay_hours.value}`}
        label="Feedback delay (hours after a run)"
        desc="How long after someone's analysis finishes before the how-did-it-go note goes out."
        unit=" hr"
        scale={1}
        step="1"
        state={email.feedback_email_delay_hours}
        busy={busy}
        onSave={(v) => apply(() => operatorSetLimit(server, token, "feedback_email_delay_hours", v))}
      />
        </>
      )}

      {section === "templates" && (
        <>
      <h3 className="operator-email-subhead">Message templates</h3>
      <p className="muted operator-email-subnote">
        One row per email, in the order a pilot user meets them. Click a row
        to edit its subject and body — anything in double braces is filled in
        when the email is sent — and send yourself a test of that one.
      </p>
      <TemplateAccordion
        templates={email.templates}
        busy={busy}
        server={server}
        token={token}
        onTestSettings={setLive}
        onSave={(name, subject, body) =>
          apply(() => operatorSetEmailTemplate(server, token, name, subject, body))
        }
        onReset={(name) =>
          apply(() => operatorSetEmailTemplate(server, token, name, null, null))
        }
      />
        </>
      )}
    </div>
  );
}

const TEMPLATE_LABELS: Record<OperatorEmailTemplateName, string> = {
  ready: "“Analysis ready” email",
  run_delayed: "“Taking longer” email (1h)",
  run_delayed_followup: "“Still working” email (3h)",
  run_delayed_final: "“Still working” email (24h)",
  run_failed: "“Analysis stopped” email",
  feedback: "Feedback email",
  nudge: "Expiry-reminder email",
  apply: "Application confirmation",
  approval: "Access-code email",
  code_expiry: "Unused-code expiry warning",
};

// When each email fires — shown on the accordion row so the operator can
// find the right draft without opening each one. Order = the order a pilot
// user meets them.
const TEMPLATE_ORDER: OperatorEmailTemplateName[] = [
  "apply",
  "approval",
  "ready",
  "run_delayed",
  "run_delayed_followup",
  "run_delayed_final",
  "run_failed",
  "nudge",
  "feedback",
  "code_expiry",
];
const TEMPLATE_WHEN: Record<OperatorEmailTemplateName, string> = {
  apply: "right after someone submits an application",
  approval: "when you approve an application, and on Resend",
  ready: "the moment a run finishes, with the download link",
  run_delayed:
    "automatically, ≈1 hour into a run that's still going — first of three keep-informed notes",
  run_delayed_followup:
    "≈3 hours in, if it's still going (e.g. riding out a provider outage)",
  run_delayed_final:
    "≈24 hours in — the last keep-informed note, a day before the 48h give-up",
  run_failed: "if a run cannot be recovered and is stopped for good",
  nudge: "the day before a saved analysis is auto-deleted",
  feedback: "a few hours after a finished run",
  code_expiry:
    "when an issued code has sat unused for a week — 48h grace, then the code is retired",
};

function TemplateAccordion({
  templates,
  busy,
  server,
  token,
  onTestSettings,
  onSave,
  onReset,
}: {
  templates: OperatorEmailSettings["templates"];
  busy: boolean;
  server: string;
  token: string;
  onTestSettings: (s: OperatorSettings) => void;
  onSave: (
    name: OperatorEmailTemplateName,
    subject: string,
    body: string,
  ) => void;
  onReset: (name: OperatorEmailTemplateName) => void;
}) {
  const [open, setOpen] = useState<OperatorEmailTemplateName | null>(null);
  return (
    <div className="operator-tpl-list">
      {TEMPLATE_ORDER
        // Skip a template the server doesn't return (frontend deployed ahead
        // of the server) — render what we have instead of white-screening.
        .filter((name) => name in templates)
        .map((name) => {
          const t = templates[name];
          const isOpen = open === name;
          return (
            <div
              key={`${name}-${t.overridden}-${t.subject}`}
              className={`operator-tpl${isOpen ? " operator-tpl--open" : ""}`}
            >
              <button
                type="button"
                className="operator-tpl-head"
                aria-expanded={isOpen}
                onClick={() => setOpen(isOpen ? null : name)}
              >
                <span className="operator-tpl-chevron" aria-hidden="true">
                  {isOpen ? "▾" : "▸"}
                </span>
                <span className="operator-tpl-name">
                  {TEMPLATE_LABELS[name]}
                  {t.overridden && (
                    <span className="operator-tpl-badge">edited</span>
                  )}
                </span>
                <span className="operator-tpl-when muted">
                  Sent {TEMPLATE_WHEN[name]}
                </span>
              </button>
              {isOpen && (
                <TemplateEditor
                  name={name}
                  template={t}
                  busy={busy}
                  server={server}
                  token={token}
                  onTestSettings={onTestSettings}
                  onSave={(subject, body) => onSave(name, subject, body)}
                  onReset={() => onReset(name)}
                />
              )}
            </div>
          );
        })}
    </div>
  );
}

function AddressRow({
  label,
  name,
  state,
  busy,
  apply,
  server,
  token,
  placeholder,
  hint,
}: {
  label: string;
  name: OperatorEmailAddressName;
  state: OperatorEmailSettings["from_address"];
  busy: boolean;
  apply: (fn: () => Promise<OperatorSettings>) => Promise<void>;
  server: string;
  token: string;
  placeholder: string;
  hint: string;
}) {
  const [input, setInput] = useState(state.value ?? "");
  const overridden = state.source === "override";
  const dirty = input.trim() !== (state.value ?? "");
  return (
    <div className="operator-key-row operator-address-row">
      <span className="operator-key-label">{label}</span>
      <input
        type="text"
        className="operator-input"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        placeholder={state.env_default ?? placeholder}
        aria-label={label}
        autoComplete="off"
      />
      <button
        type="button"
        className="operator-row-action"
        disabled={busy || !dirty || !input.trim()}
        onClick={() => apply(() => operatorSetEmailAddress(server, token, name, input.trim()))}
      >
        Save
      </button>
      {overridden && (
        <button
          type="button"
          className="operator-row-action operator-danger"
          disabled={busy}
          onClick={() => apply(() => operatorSetEmailAddress(server, token, name, null))}
        >
          Revert
        </button>
      )}
      {dirty ? (
        <span className="operator-unsaved">Unsaved</span>
      ) : overridden ? (
        <span className="operator-field-note muted">Custom — “Revert” restores the built-in default.</span>
      ) : null}
      <span className="operator-field-hint muted">{hint}</span>
    </div>
  );
}

// One setting = one switch. No "Turn on/off" + badge + "Revert" cluster, and no
// "· override" jargon. `dangerWhenOn` colours the switch by MEANING rather than
// by the raw boolean — fake-email ON is an attention state, feedback ON is calm —
// so the colour can't read backwards.
//
// NOTHING in this row may mount/unmount or change width on click (Rich,
// 2026-07-02): the old per-row `reset` link appeared when an override took
// effect, so the whole control cluster shifted under the pointer mid-click —
// which caused an ACCIDENTAL fake-email-off flip on a real-key box. The reset
// affordance is deliberately gone ("don't need reset"); flipping the switch
// back is the way back, and each deploy's env default still shows in the
// Settings describe if anyone needs the baseline.
function FlagToggleRow({
  label,
  desc,
  state,
  busy,
  onToggle,
  dangerWhenOn = false,
}: {
  label: string;
  desc: string;
  state: OperatorEmailSettings["use_fake_email"];
  busy: boolean;
  onToggle: (value: boolean) => void;
  dangerWhenOn?: boolean;
}) {
  const on = state.value;
  const attention = dangerWhenOn && on; // ON-when-dangerous → coloured switch
  const cls = [
    "operator-switch",
    on ? "is-on" : "is-off",
    attention ? "is-attention" : "",
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <div className="operator-switch-row">
      <div className="operator-switch-text">
        <span className="operator-switch-label">{label}</span>
        <span className="operator-switch-desc muted">{desc}</span>
      </div>
      <div className="operator-switch-control">
        <button
          type="button"
          role="switch"
          aria-checked={on}
          aria-label={label}
          className={cls}
          disabled={busy}
          onClick={() => onToggle(!on)}
        >
          <span className="operator-switch-track" aria-hidden="true">
            <span className="operator-switch-knob" />
          </span>
          <span className="operator-switch-state">{on ? "On" : "Off"}</span>
        </button>
      </div>
    </div>
  );
}

function TemplateEditor({
  name,
  template,
  busy,
  server,
  token,
  onTestSettings,
  onSave,
  onReset,
}: {
  name: OperatorEmailTemplateName;
  template: OperatorEmailSettings["templates"][OperatorEmailTemplateName];
  busy: boolean;
  server: string;
  token: string;
  onTestSettings: (s: OperatorSettings) => void;
  onSave: (subject: string, body: string) => void;
  onReset: () => void;
}) {
  const [subject, setSubject] = useState(template.subject);
  const [body, setBody] = useState(template.body);
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const dirty = subject !== template.subject || body !== template.body;

  // Test-send lives in the editor (not a separate picker): the row already
  // knows its own `name`, so "send a test" can only target the template you're
  // looking at — which removes the duplicate all-templates <select>.
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<string | null>(null);

  async function sendTest() {
    if (testing || !testTo.trim()) return;
    setTesting(true);
    setTestResult(null);
    try {
      const r = await operatorSendTestEmail(server, token, name, testTo.trim());
      onTestSettings(r.settings);
      setTestResult(
        r.faked
          ? "Logged (fake-email mode is on — nothing was delivered)."
          : r.ok
            ? `Sent to ${testTo.trim()}.`
            : `Send failed: ${r.error ?? "unknown error"}`,
      );
    } catch (e) {
      setTestResult(e instanceof Error ? e.message : String(e));
    } finally {
      setTesting(false);
    }
  }

  // Drop {{token}} in at the caret (replacing any selection), then put the
  // caret just past it. This is what keeps placeholders recoverable — even if
  // the operator deletes one while editing, every available token is one click
  // away below the body.
  function insertToken(tok: string) {
    const marker = `{{${tok}}}`;
    const el = bodyRef.current;
    const start = el?.selectionStart ?? body.length;
    const end = el?.selectionEnd ?? body.length;
    setBody(body.slice(0, start) + marker + body.slice(end));
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      const caret = start + marker.length;
      el.setSelectionRange(caret, caret);
    });
  }

  return (
    <div className="operator-key-row operator-template-row">
      {dirty && <span className="operator-unsaved">Unsaved changes</span>}

      <span className="operator-field-label muted">Subject</span>
      <input
        type="text"
        className="operator-input operator-template-subject"
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
        aria-label={`${TEMPLATE_LABELS[name]} subject`}
        placeholder="Subject"
      />

      <span className="operator-field-label muted">Body</span>
      <textarea
        ref={bodyRef}
        className="operator-input operator-template-body"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        aria-label={`${TEMPLATE_LABELS[name]} body`}
        rows={8}
      />

      {template.tokens.length > 0 && (
        <div className="operator-token-row">
          <span className="operator-token-label muted">Placeholders — click to insert:</span>
          {template.tokens.map((t) => (
            <button
              key={t}
              type="button"
              className="operator-token-chip"
              onClick={() => insertToken(t)}
              title={`Insert {{${t}}} at the cursor`}
            >
              {`{{${t}}}`}
            </button>
          ))}
        </div>
      )}

      <div className="operator-template-actions">
        <button
          type="button"
          className="operator-row-action"
          disabled={busy || !dirty || !subject.trim() || !body.trim()}
          onClick={() => onSave(subject, body)}
        >
          Save
        </button>
        {template.overridden && (
          <button
            type="button"
            className="operator-row-action operator-danger"
            disabled={busy}
            onClick={onReset}
          >
            Reset to default
          </button>
        )}
      </div>

      <div className="operator-template-test">
        <span className="operator-field-label muted">
          Send a test of this email
        </span>
        <input
          type="text"
          className="operator-input"
          value={testTo}
          onChange={(e) => setTestTo(e.target.value)}
          placeholder="you@example.com"
          aria-label={`Send a test ${TEMPLATE_LABELS[name]} to`}
          autoComplete="off"
        />
        <button
          type="button"
          className="operator-row-action"
          disabled={busy || testing || !testTo.trim()}
          onClick={sendTest}
        >
          {testing ? "Sending…" : "Send test"}
        </button>
        {testResult && (
          <span className="operator-field-hint muted">{testResult}</span>
        )}
      </div>
    </div>
  );
}

// One editable numeric cap. `scale` maps the stored unit ↔ a friendlier
// display unit (budget/rate are 1:1; upload shows MB over stored bytes). The
// pill shows the authoritative live value + whether it's an override; the
// parent re-keys this row so the input re-inits after a save/revert.
export function LimitRow({
  label,
  desc,
  unit,
  scale,
  step,
  state,
  busy,
  onSave,
}: {
  label: string;
  /** One plain-words line on what this limit actually does — no internal
   *  vocabulary. Rendered under the label so the number is never a mystery. */
  desc: string;
  unit: string;
  scale: number;
  step: string;
  state: OperatorLimitState;
  busy: boolean;
  onSave: (storeValue: number | null) => void;
}) {
  const display = (v: number | null): number | null =>
    v == null ? null : Math.round((v / scale) * 100) / 100;
  const liveDisplay = display(state.value);
  const defDisplay = display(state.env_default);
  const [input, setInput] = useState(liveDisplay != null ? String(liveDisplay) : "");
  const n = Number(input);
  const valid = input.trim() !== "" && !Number.isNaN(n) && n > 0;
  // Money reads "$300", never "300$"; unit-suffix values keep their suffix
  // ("128 MB", "20/hr").
  const fmt = (v: number | null): string =>
    v == null ? "none" : unit === "$" ? `$${v}` : `${v}${unit}`;
  return (
    <div className="operator-key-row operator-limit-row">
      <div className="operator-limit-text">
        <span className="operator-key-label">{label}</span>
        <p className="operator-limit-desc">{desc}</p>
      </div>
      <StatePill tone={state.source === "override" ? "warn" : "ok"}>
        {state.source === "override" ? "Override" : "Default"} · {fmt(liveDisplay)}
      </StatePill>
      <input
        type="number"
        className="operator-input"
        value={input}
        step={step}
        min="0"
        onChange={(e) => setInput(e.target.value)}
        aria-label={label}
      />
      <button
        type="button"
        className="operator-row-action"
        disabled={busy || !valid}
        onClick={() => onSave(Math.round(n * scale * 100) / 100)}
      >
        Save
      </button>
      {state.source === "override" && (
        <button
          type="button"
          className="operator-row-action operator-danger"
          disabled={busy}
          onClick={() => onSave(null)}
        >
          Revert{defDisplay != null ? ` (${fmt(defDisplay)})` : ""}
        </button>
      )}
    </div>
  );
}


// Operator-editable tier prices. Loads its own /operator/pricing snapshot
// (separate endpoint from /operator/settings) and renders one row per
// purchasable tier. A saved override flows to BOTH the cost screen and the
// Stripe charge; Revert returns the tier to the built-in price.
export function PricingSection({ server, token }: { server: string; token: string }) {
  const loader = useCallback(() => operatorGetPricing(server, token), [server, token]);
  const { state, reload } = useLoader<OperatorPricing>(loader);
  const [live, setLive] = useState<OperatorPricing | null>(null);
  const pricing = live ?? (state.kind === "ready" ? state.data : null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save(name: string, value: number | null) {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      setLive(await operatorSetTierPrice(server, token, name, value));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="operator-limits operator-pricing">
      <div className="operator-pricing-head">
        <h3 className="operator-limits-head">Tier pricing</h3>
        <button
          type="button"
          className="operator-reload operator-reload-sm"
          onClick={() => {
            setLive(null);
            reload();
          }}
          disabled={state.kind === "loading"}
        >
          {state.kind === "loading" ? "Loading…" : "Reload"}
        </button>
      </div>
      <p className="muted">
        What each cloud tier charges. Applies to the next checkout — no redeploy.
        Revert returns a tier to the built-in price.
      </p>
      {error && <div className="operator-error">Action failed: {error}</div>}
      {state.kind === "error" && !pricing && (
        <div className="operator-error">Could not load pricing: {state.message}</div>
      )}
      {pricing?.tiers.map((t) => (
        <TierPriceRow
          // re-init the input from the authoritative value after a save/revert
          key={`${t.name}-${t.override ?? "def"}-${t.effective}`}
          tier={t}
          min={pricing.min}
          max={pricing.max}
          busy={busy}
          onSave={(v) => save(t.name, v)}
        />
      ))}
    </div>
  );
}

function TierPriceRow({
  tier,
  min,
  max,
  busy,
  onSave,
}: {
  tier: OperatorTierPrice;
  min: number;
  max: number;
  busy: boolean;
  onSave: (value: number | null) => void;
}) {
  const isOverride = tier.override != null;
  const [input, setInput] = useState(String(tier.effective));
  const n = Number(input);
  const valid =
    input.trim() !== "" && Number.isInteger(n) && n >= min && n <= max;
  // The hint reflects the CURRENT effective price's economics (updates on save).
  const underwater = tier.status === "underwater" || tier.status === "thin";
  return (
    <div className="operator-key-row operator-limit-row">
      <span className="operator-key-label">{tier.name}</span>
      <StatePill tone={isOverride ? "warn" : "ok"}>
        {isOverride ? "Override" : "Default"} · ${tier.effective}
      </StatePill>
      <input
        type="number"
        className="operator-input"
        value={input}
        step="1"
        min={min}
        max={max}
        onChange={(e) => setInput(e.target.value)}
        aria-label={`${tier.name} price (USD)`}
      />
      <button
        type="button"
        className="operator-row-action"
        disabled={busy || !valid}
        onClick={() => onSave(Math.round(n))}
      >
        Save
      </button>
      {isOverride && (
        <button
          type="button"
          className="operator-row-action operator-danger"
          disabled={busy}
          onClick={() => onSave(null)}
        >
          Revert (${tier.code_default})
        </button>
      )}
      {tier.recommended != null && (
        <span className={`operator-price-hint${underwater ? " is-warn" : ""}`}>
          {underwater
            ? `⚠ below cost — rec ≥ $${tier.recommended}`
            : `rec $${tier.recommended}`}
        </span>
      )}
      {tier.note && <span className="operator-price-hint">{tier.note}</span>}
    </div>
  );
}


type PanelChromeProps<T> = {
  title: string;
  subtitle: string;
  state: LoadState<T[]>;
  onReload: () => void;
  empty: string;
  renderTable: (rows: T[]) => React.ReactNode;
  // Optional content rendered inside the card, between the header and the
  // table/empty state — e.g. a create affordance that belongs to the panel
  // rather than floating above it. Omitted by every other panel (no change).
  bodyPreface?: React.ReactNode;
};

function PanelChrome<T>({
  title,
  subtitle,
  state,
  onReload,
  empty,
  renderTable,
  bodyPreface,
}: PanelChromeProps<T>) {
  return (
    <div className="operator-panel">
      <div className="operator-panel-head">
        <div>
          <h2>{title}</h2>
          <p className="muted">{subtitle}</p>
        </div>
        <button
          type="button"
          className="operator-reload"
          onClick={onReload}
          disabled={state.kind === "loading"}
        >
          {state.kind === "loading" ? "Loading…" : "Reload"}
        </button>
      </div>
      {bodyPreface}
      {state.kind === "error" && (
        <div className="operator-error">Could not load: {state.message}</div>
      )}
      {state.kind === "ready" && state.data.length === 0 && (
        <div className="operator-empty">{empty}</div>
      )}
      {state.kind === "ready" && state.data.length > 0 &&
        renderTable(state.data)}
      {state.kind === "loading" && (
        <div className="operator-loading">Loading…</div>
      )}
    </div>
  );
}


// --- Audit panel ---


const AUDIT_PAGE_SIZE = 100;


function AuditPanel({ server, token }: { server: string; token: string }) {
  const [page, setPage] = useState(0);
  const loader = useCallback(
    () =>
      operatorListAuditLog(server, token, {
        limit: AUDIT_PAGE_SIZE,
        offset: page * AUDIT_PAGE_SIZE,
      }).then((r) => r.entries),
    [server, token, page],
  );
  const { state, reload } = useLoader<OperatorAuditEntry[]>(loader);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  function toggle(id: number) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // Page-size signal: if a full page came back, there *might* be more.
  // Cheap approximation that avoids a second count() query on the server.
  const hasMaybeMore =
    state.kind === "ready" && state.data.length === AUDIT_PAGE_SIZE;

  return (
    <PanelChrome
      title="Audit log"
      subtitle="Mutating operator actions, most recent first. Each row carries the verified operator email (or 'unknown' if Cloudflare Access JWT isn't present)."
      state={state}
      onReload={reload}
      empty="No audit entries yet."
      renderTable={(rows) => (
        <>
          <table className="operator-table operator-audit-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Actor</th>
                <th>Action</th>
                <th>Target</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((e) => {
                const open = expanded.has(e.id);
                const hasDetails = e.details && Object.keys(e.details).length > 0;
                return (
                  <tr key={e.id}>
                    <td>{formatLocalTime(e.recorded_at)}</td>
                    <td>{e.actor}</td>
                    <td>{e.action}</td>
                    <td>
                      {e.target_kind}
                      <br />
                      <code className="operator-audit-target">
                        {e.target_id}
                      </code>
                    </td>
                    <td className="operator-audit-details">
                      {hasDetails ? (
                        <>
                          <button
                            type="button"
                            className="operator-row-action"
                            onClick={() => toggle(e.id)}
                            aria-expanded={open}
                          >
                            {open ? "Hide" : "Show"}
                          </button>
                          {open && (
                            <pre className="operator-audit-details-pre">
                              {JSON.stringify(e.details, null, 2)}
                            </pre>
                          )}
                        </>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="operator-audit-pager">
            <button
              type="button"
              className="operator-row-action"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              ← Newer
            </button>
            <span className="muted">Page {page + 1}</span>
            <button
              type="button"
              className="operator-row-action"
              onClick={() => setPage((p) => p + 1)}
              disabled={!hasMaybeMore}
            >
              Older →
            </button>
          </div>
        </>
      )}
    />
  );
}


// --- Live runs panel ------------------------------------------------------
//
// In-flight runs at a glance. Consumes the operator-authed
// /operator/runs (list) + /operator/runs/{id}/status (per-run live detail).
// Polling-driven on purpose: the X-Operator-Token header authenticates every
// request, which an EventSource can't carry, so the richer SSE event tail
// (/operator/runs/{id}/events — CF-cookie auth, prod-only) is a deliberate
// follow-up. v1 shows the coarse lifecycle state, timing, queue position and
// cancel; the fine stage rail + cost-so-far need the event stream.

// In-flight for the Live runs view + nav badge — INCLUDES "queued" (a queued run
// is shown and cancellable here). Distinct from ACTIVE_RUN_STATUSES (no "queued")
// used by Run history's auto-poll + inline cancel.
const IN_FLIGHT_STATUSES = new Set(["queued", "initializing", "running"]);
const LIVE_CHAIN_STATES = new Set(["queued", "initializing", "running"]);
const LIVE_RUNS_POLL_MS = 4000;
const LIVE_DETAIL_POLL_MS = 3000;
const LIVE_LAYOUT_KEY = "aurora.operator.liveRunsLayout";

type LiveLayout = "rows" | "cards";

function readLiveLayout(): LiveLayout {
  try {
    return localStorage.getItem(LIVE_LAYOUT_KEY) === "cards" ? "cards" : "rows";
  } catch {
    return "rows";
  }
}

function formatDuration(secs: number): string {
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  const mm = String(m).padStart(2, "0");
  const ss = String(s).padStart(2, "0");
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

// Elapsed since an ISO start, ticking each second. "—" until a valid start.
function useElapsed(startIso: string | null | undefined): string {
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);
  if (!startIso) return "—";
  const start = new Date(startIso).getTime();
  if (Number.isNaN(start)) return "—";
  return formatDuration(Math.max(0, Math.floor((Date.now() - start) / 1000)));
}

function LiveBadge({ status }: { status: string }) {
  const variant =
    status === "running"
      ? " live-badge--running"
      : status === "queued" || status === "initializing"
        ? " live-badge--queued"
        : "";
  return <span className={`live-badge${variant}`}>{status}</span>;
}

function LiveRunsPanel({ server, token }: { server: string; token: string }) {
  const [selected, setSelected] = useState<string | null>(null);
  const [layout, setLayout] = useState<LiveLayout>(readLiveLayout);

  const onLayout = useCallback((l: LiveLayout) => {
    setLayout(l);
    try {
      localStorage.setItem(LIVE_LAYOUT_KEY, l);
    } catch {
      /* sessionStorage/localStorage unavailable — in-memory only */
    }
  }, []);

  if (selected) {
    return (
      <LiveRunDetail
        server={server}
        token={token}
        cloudRunId={selected}
        onBack={() => setSelected(null)}
        onOpen={setSelected}
      />
    );
  }
  return (
    <LiveRunsList
      server={server}
      token={token}
      layout={layout}
      onLayout={onLayout}
      onOpen={setSelected}
    />
  );
}

type LiveRowActions = {
  cancelling: string | null;
  onOpen: (id: string) => void;
  onCancel: (run: OperatorRunRecord) => void;
};

function LiveRunsList({
  server,
  token,
  layout,
  onLayout,
  onOpen,
}: {
  server: string;
  token: string;
  layout: LiveLayout;
  onLayout: (l: LiveLayout) => void;
  onOpen: (id: string) => void;
}) {
  const loader = useCallback(
    () =>
      operatorListRuns(server, token).then((r) =>
        r.runs
          .filter((x) => IN_FLIGHT_STATUSES.has(x.status))
          .sort((a, b) =>
            (b.started_at ?? "").localeCompare(a.started_at ?? ""),
          ),
      ),
    [server, token],
  );
  const { state, reload, reloadQuiet } = useLoader<OperatorRunRecord[]>(loader);

  // The live view polls while mounted — runs appear and complete on their own.
  useEffect(() => {
    const t = setInterval(reloadQuiet, LIVE_RUNS_POLL_MS);
    return () => clearInterval(t);
  }, [reloadQuiet]);

  const [cancelling, setCancelling] = useState<string | null>(null);
  function onCancel(run: OperatorRunRecord) {
    const label = run.access_code
      ? `${run.cloud_run_id} (${run.access_code})`
      : run.cloud_run_id;
    if (!window.confirm(cancelRunConfirm(label))) {
      return;
    }
    setCancelling(run.cloud_run_id);
    operatorCancelRun(server, token, run.cloud_run_id)
      .then(() => reloadQuiet())
      .catch((e: unknown) =>
        window.alert(
          `Cancel failed: ${e instanceof Error ? e.message : String(e)}`,
        ),
      )
      .finally(() => setCancelling(null));
  }

  const actions: LiveRowActions = { cancelling, onOpen, onCancel };

  return (
    <div className="operator-panel">
      <div className="operator-panel-head">
        <div>
          <h2>Live runs</h2>
          <p className="muted">
            Analyses in flight right now. Completed runs move to Run history.
            Auto-refreshes every {LIVE_RUNS_POLL_MS / 1000}s.
          </p>
        </div>
        <div className="live-head-actions">
          <div className="live-seg-group" role="group" aria-label="Layout">
            <button
              type="button"
              className={`live-seg${layout === "rows" ? " active" : ""}`}
              aria-pressed={layout === "rows"}
              onClick={() => onLayout("rows")}
            >
              Rows
            </button>
            <button
              type="button"
              className={`live-seg${layout === "cards" ? " active" : ""}`}
              aria-pressed={layout === "cards"}
              onClick={() => onLayout("cards")}
            >
              Cards
            </button>
          </div>
          <button
            type="button"
            className="operator-reload"
            onClick={reload}
            disabled={state.kind === "loading"}
          >
            {state.kind === "loading" ? "Loading…" : "Reload"}
          </button>
        </div>
      </div>

      {state.kind === "error" && (
        <div className="operator-error">Could not load: {state.message}</div>
      )}
      {state.kind === "loading" && (
        <div className="operator-loading">Loading…</div>
      )}
      {state.kind === "ready" && state.data.length === 0 && (
        <div className="operator-empty">No runs in flight.</div>
      )}
      {state.kind === "ready" &&
        state.data.length > 0 &&
        (layout === "rows" ? (
          <div className="live-rows">
            {state.data.map((run) => (
              <LiveRunRow key={run.id} run={run} {...actions} />
            ))}
          </div>
        ) : (
          <div className="live-cards">
            {state.data.map((run) => (
              <LiveRunCard key={run.id} run={run} {...actions} />
            ))}
          </div>
        ))}
    </div>
  );
}

function LiveRunRow({
  run,
  cancelling,
  onOpen,
  onCancel,
}: { run: OperatorRunRecord } & LiveRowActions) {
  const elapsed = useElapsed(run.started_at);
  return (
    <div className="live-row">
      <div className="live-row-id">
        <code>{run.cloud_run_id}</code>
        {run.access_code && <span className="live-code">{run.access_code}</span>}
      </div>
      <LiveBadge status={run.status} />
      <div className="live-row-meta">
        {(run.owner_name || run.owner_email || run.notify_email) && (
          <span>
            Owner <b>{run.owner_name ?? run.owner_email ?? run.notify_email}</b>
          </span>
        )}
        <span>Started {formatLocalTime(run.started_at)}</span>
        <span>
          Elapsed <b>{elapsed}</b>
        </span>
        {run.journey_preview_cost_usd != null && (
          // One row = one journey: the consumed preview is folded in here.
          <span>
            preview ✓ <b>${run.journey_preview_cost_usd.toFixed(2)}</b>
          </span>
        )}
      </div>
      <div className="live-row-actions">
        <button
          type="button"
          className="operator-row-action"
          onClick={() => onOpen(run.cloud_run_id)}
        >
          Open
        </button>
        <button
          type="button"
          className="operator-row-action operator-row-action--danger"
          disabled={cancelling === run.cloud_run_id}
          onClick={() => onCancel(run)}
        >
          {cancelling === run.cloud_run_id ? "Cancelling…" : "Cancel"}
        </button>
      </div>
    </div>
  );
}

function LiveRunCard({
  run,
  cancelling,
  onOpen,
  onCancel,
}: { run: OperatorRunRecord } & LiveRowActions) {
  const elapsed = useElapsed(run.started_at);
  return (
    <div className="live-card-item">
      <div className="live-card-top">
        <code>{run.cloud_run_id}</code>
        <LiveBadge status={run.status} />
      </div>
      {run.access_code && <div className="live-code">{run.access_code}</div>}
      {run.journey_preview_cost_usd != null && (
        // One card = one journey: the consumed preview rides as a done lane.
        <div className="live-journey-lane">
          <span className="live-lane-seg live-lane-seg--done">
            preview ✓ ${run.journey_preview_cost_usd.toFixed(2)}
          </span>
          <span className="live-lane-arrow" aria-hidden="true">→</span>
          <span className="live-lane-seg">{run.kind ?? "full"} analysis</span>
        </div>
      )}
      <dl className="live-card-meta">
        {(run.owner_name || run.owner_email || run.notify_email) && (
          <div>
            <dt>Owner</dt>
            <dd>{run.owner_name ?? run.owner_email ?? run.notify_email}</dd>
          </div>
        )}
        <div>
          <dt>Started</dt>
          <dd>{formatLocalTime(run.started_at)}</dd>
        </div>
        <div>
          <dt>Elapsed</dt>
          <dd>
            <b>{elapsed}</b>
          </dd>
        </div>
      </dl>
      <div className="live-card-actions">
        <button
          type="button"
          className="operator-row-action"
          onClick={() => onOpen(run.cloud_run_id)}
        >
          Open
        </button>
        <button
          type="button"
          className="operator-row-action operator-row-action--danger"
          disabled={cancelling === run.cloud_run_id}
          onClick={() => onCancel(run)}
        >
          {cancelling === run.cloud_run_id ? "Cancelling…" : "Cancel"}
        </button>
      </div>
    </div>
  );
}

// Ordered fan-out rail. progress {stage} marks a stage running; stage_done
// (discovery/fanout/audit) carries its stage_cost_usd. See events.py.
const RAIL_STAGES = ["warmup", "discovery", "fanout", "synthesis", "audit"] as const;
// A preview chain (--only-discovery) runs ONLY warmup + discovery — it never
// fans out, synthesizes, or audits. Rendering the full 5-stage rail on a preview
// detail makes a healthy, finished preview look like a full run stalled at
// discovery (fanout/synthesis/audit stuck on "todo").
const PREVIEW_RAIL_STAGES = ["warmup", "discovery"] as const;

type StageState = "done" | "now" | "todo";
type LiveStreamStatus = "connecting" | "live" | "unavailable";

// A stage is "done" once a later stage starts (or its own stage_done lands);
// the latest-started stage is "now"; run_complete collapses all to done.
export function deriveStageStates(
  events: SseEvent[],
  chainState: string | undefined,
): Record<string, StageState> {
  const started = new Set<string>();
  const finished = new Set<string>();
  for (const ev of events) {
    const stage = (ev.data as { stage?: string } | null)?.stage;
    if (!stage) continue;
    if (ev.type === "progress") started.add(stage);
    if (ev.type === "stage_done") {
      started.add(stage);
      finished.add(stage);
    }
  }
  let maxStarted = -1;
  RAIL_STAGES.forEach((s, i) => {
    if (started.has(s)) maxStarted = Math.max(maxStarted, i);
  });
  const allDone = chainState === "run_complete";
  const out: Record<string, StageState> = {};
  RAIL_STAGES.forEach((s, i) => {
    out[s] = allDone
      ? "done"
      : i < maxStarted
        ? "done"
        : i === maxStarted
          ? finished.has(s)
            ? "done"
            : "now"
          : "todo";
  });
  return out;
}

// Cost-so-far = sum of stage_done.stage_cost_usd seen on the stream. Honest:
// it reflects only the stages that have reported, not a projected total.
export function costSoFar(events: SseEvent[]): number {
  return events.reduce((sum, ev) => {
    if (ev.type !== "stage_done") return sum;
    const c = (ev.data as { stage_cost_usd?: number } | null)?.stage_cost_usd;
    return sum + (typeof c === "number" ? c : 0);
  }, 0);
}

// Fan-out convergence count ("k of N patterns done") from the stream. Sourced
// from the active-set emit, not the Batches API — see fanout.py. Takes the
// high-water mark (max `done`), not the latest event: a RESUMED run rebuilds its
// active set and re-emits 1/N, 2/N… from scratch, so "latest" would jump the rail
// backward; max holds the peak until the resumed run climbs past it.
// Latest synthesis sub-stage — the engine runs two serial steps (A: thread
// proposer, B: grand + intro); surface which one the silent stage is on.
export function synthesisStep(
  events: SseEvent[],
): { step: string; status: string } | null {
  let last: { step: string; status: string } | null = null;
  for (const ev of events) {
    if (ev.type !== "synthesis_progress") continue;
    const d = ev.data as { step?: string; status?: string };
    if (d.step) last = { step: d.step, status: d.status ?? "running" };
  }
  return last;
}

export function fanoutConverged(
  events: SseEvent[],
  eventType: "fanout_progress" | "audit_progress" = "fanout_progress",
): { done: number; total: number } | null {
  let best: { done: number; total: number } | null = null;
  for (const ev of events) {
    if (ev.type !== eventType) continue;
    const d = ev.data as { done?: number; total?: number } | null;
    if (d && typeof d.done === "number" && typeof d.total === "number") {
      if (!best || d.done > best.done) best = { done: d.done, total: d.total };
    }
  }
  return best;
}

// Per-pattern fan-out detail from the stream (pattern_progress events —
// events.py parses the chain's `[fanout] pattern <id> <status>` markers,
// emitted by run.py's callbacks on both the sync and batched transports).
// Latest status wins per pattern; order = first appearance (the order the
// fan-out started them). A resumed run re-emits from scratch — later events
// simply overwrite the same ids, so the strip converges to the truth.
export type PatternProgress = {
  id: string;
  status: "running" | "done" | "error";
  quotes?: number;
  costUsd?: number;
  // Mid-pattern heartbeat (status=progress events): tool calls so far.
  toolCalls?: number;
  // Server event timestamps (ms) — survive replay/reconnect, unlike client
  // receipt time. Absent on pre-ts events; the chip just skips the clock.
  startedAtMs?: number;
  endedAtMs?: number;
};

function _evMs(d: { _ts?: string } | null): number | undefined {
  const t = d?._ts ? Date.parse(d._ts) : NaN;
  return Number.isFinite(t) ? t : undefined;
}

export function derivePatternProgress(events: SseEvent[]): PatternProgress[] {
  const byId = new Map<string, PatternProgress>();
  for (const ev of events) {
    if (ev.type !== "pattern_progress") continue;
    const d = ev.data as {
      pattern_id?: string;
      status?: string;
      quotes?: number;
      cost_usd?: number;
      tool_calls?: number;
      _ts?: string;
    } | null;
    if (!d?.pattern_id) continue;
    const prev = byId.get(d.pattern_id);
    if (d.status === "progress") {
      // Heartbeat: keep (or establish) running; carry the call count.
      byId.set(d.pattern_id, {
        id: d.pattern_id,
        status: prev?.status === "done" || prev?.status === "error"
          ? prev.status // stale heartbeat after terminal — don't resurrect
          : "running",
        quotes: prev?.quotes,
        costUsd: prev?.costUsd,
        toolCalls: typeof d.tool_calls === "number" ? d.tool_calls : prev?.toolCalls,
        startedAtMs: prev?.startedAtMs ?? _evMs(d),
        endedAtMs: prev?.endedAtMs,
      });
      continue;
    }
    if (d.status !== "running" && d.status !== "done" && d.status !== "error")
      continue;
    byId.set(d.pattern_id, {
      id: d.pattern_id,
      status: d.status,
      quotes: typeof d.quotes === "number" ? d.quotes : prev?.quotes,
      costUsd: typeof d.cost_usd === "number" ? d.cost_usd : prev?.costUsd,
      toolCalls: prev?.toolCalls,
      startedAtMs:
        d.status === "running" ? (prev?.startedAtMs ?? _evMs(d)) : prev?.startedAtMs,
      endedAtMs:
        d.status === "done" || d.status === "error"
          ? (_evMs(d) ?? prev?.endedAtMs)
          : prev?.endedAtMs,
    });
  }
  // STABLE insertion order — a live wall must not shuffle. The first cut
  // sorted running-first, and every completion physically moved chips out
  // from under the operator's eyes (Rich, live 2026-07-02). Status is color;
  // position is identity.
  return [...byId.values()];
}

// Fan-out pattern costs land per-pattern BEFORE the fanout stage_done rolls
// them up. While fanout is still running, add the done-pattern costs on top
// of the stage sums so "so far" moves during the longest stage; once the
// fanout stage_done arrives (its cost includes the patterns), stop — no
// double count.
// m:ss (or h:mm:ss) from a millisecond duration — per-chip elapsed clocks.
export function formatClock(ms: number): string {
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const sec = total % 60;
  const two = (n: number) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${two(m)}:${two(sec)}` : `${m}:${two(sec)}`;
}

export function patternCostInFlight(events: SseEvent[]): number {
  const fanoutStageDone = events.some(
    (ev) =>
      ev.type === "stage_done" &&
      (ev.data as { stage?: string } | null)?.stage === "fanout",
  );
  if (fanoutStageDone) return 0;
  return derivePatternProgress(events).reduce(
    (sum, p) => sum + (p.status === "done" && p.costUsd ? p.costUsd : 0),
    0,
  );
}

function formatLiveEvent(ev: SseEvent): string {
  const d = (ev.data ?? {}) as Record<string, unknown>;
  switch (ev.type) {
    case "progress":
      return `${d.stage ?? ""}`.trim();
    case "stage_done":
      return `${d.stage ?? ""} · $${Number(d.stage_cost_usd ?? 0).toFixed(2)}`;
    case "fanout_progress":
      return `${d.done ?? 0} of ${d.total ?? 0} converged`;
    case "audit_progress":
      return `${d.done ?? 0} of ${d.total ?? 0} checked`;
    case "synthesis_progress":
      return `stage ${d.step ?? "?"} ${d.status === "done" ? "done" : ""} — ${d.detail ?? ""}`.replace(/\s+/g, " ").trim();
    case "pattern_progress":
      return d.status === "progress"
        ? `${d.pattern_id ?? ""} · ${d.tool_calls ?? "?"} tool calls`
        : `${d.pattern_id ?? ""} ${d.status ?? ""}`.trim();
    case "cost_estimate":
      return `$${Number(d.estimated_total_usd ?? 0).toFixed(2)}`;
    case "error":
      return String(d.detail ?? "");
    case "terminal_state":
      return String(d.chain_state ?? "");
    default:
      return "";
  }
}

// Subscribe to the operator-authed SSE stream for one run. Degrades to
// "unavailable" when the stream can't open (dev / no CF Access) so the caller
// falls back to the polled /status view rather than showing a broken panel.
function useOperatorRunEvents(
  server: string,
  cloudRunId: string,
): { status: LiveStreamStatus; events: SseEvent[] } {
  const [events, setEvents] = useState<SseEvent[]>([]);
  const [status, setStatus] = useState<LiveStreamStatus>("connecting");
  useEffect(() => {
    setEvents([]);
    setStatus("connecting");
    const stop = subscribeOperatorEvents({
      server,
      runId: cloudRunId,
      onEvent: (ev) => {
        setStatus("live");
        setEvents((prev) => [...prev, ev]);
      },
      onError: () => setStatus("unavailable"),
    });
    return stop;
  }, [server, cloudRunId]);
  return { status, events };
}

// Auto-follow rule (live report, 2026-07-04): the Live-runs LIST already
// folds a consumed preview into its successor's row (operator_store.
// list_runs_for_display); the DETAIL view should follow the same
// philosophy — the operator is watching an ANALYSIS, not a row. Once a
// PREVIEW run's detail has both (a) reached a terminal state and (b) been
// linked to a successor analysis run on the same access code
// (server-computed `successor_run_id`, journey_links in operator_store.py,
// refreshed on every /status poll — no new endpoint needed), swap the view
// forward. Returns the id to follow, or null to stay put: still running,
// an orphaned preview (no successor ever showed up), or not a preview at
// all. Terminal is "not one of the LIVE_CHAIN_STATES" — the same
// definition the cancel button already relies on (`cancellable` below).
export function nextFollowTarget(
  status: Pick<
    StatusResponse,
    "chain_kind" | "chain_state" | "successor_run_id"
  > | null,
  currentCloudRunId: string,
): string | null {
  if (!status) return null;
  if (status.chain_kind !== "preview") return null;
  if (LIVE_CHAIN_STATES.has(status.chain_state)) return null; // not terminal yet
  const succ = status.successor_run_id;
  if (!succ || succ === currentCloudRunId) return null;
  return succ;
}

export function LiveRunDetail({
  server,
  token,
  cloudRunId,
  onBack,
  onOpen,
}: {
  server: string;
  token: string;
  cloudRunId: string;
  onBack: () => void;
  /** Navigate to a sibling run — driven both by the manual "Open the full
   *  run →" signpost below and by the auto-follow effect (nextFollowTarget)
   *  once a viewed preview goes terminal with a linked successor. */
  onOpen: (cloudRunId: string) => void;
}) {
  const loader = useCallback(
    () => operatorRunStatus(server, token, cloudRunId),
    [server, token, cloudRunId],
  );
  const { state, reloadQuiet } = useLoader<StatusResponse>(loader);

  useEffect(() => {
    const t = setInterval(reloadQuiet, LIVE_DETAIL_POLL_MS);
    return () => clearInterval(t);
  }, [reloadQuiet]);

  const status = state.kind === "ready" ? state.data : null;
  const elapsed = useElapsed(status?.started_at);

  const [cancelling, setCancelling] = useState(false);
  function onCancel() {
    if (!window.confirm(cancelRunConfirm(cloudRunId))) {
      return;
    }
    setCancelling(true);
    operatorCancelRun(server, token, cloudRunId)
      .then(() => reloadQuiet())
      .catch((e: unknown) =>
        window.alert(
          `Cancel failed: ${e instanceof Error ? e.message : String(e)}`,
        ),
      )
      .finally(() => setCancelling(false));
  }

  // Pre-run estimate, if the chain stamped it into meta.json (cost_estimate
  // persistence, 2026-06-24). Defensive reads — meta shape is server-owned.
  const meta = (status?.chain_meta ?? null) as Record<string, unknown> | null;
  const est = (meta?.cost_estimate_prerun ?? null) as
    | { total?: number; low?: number; high?: number }
    | null;

  const cancellable =
    status !== null && LIVE_CHAIN_STATES.has(status.chain_state);

  // Auto-follow: once THIS preview's poll reports a terminal state + a
  // linked successor, swap the view forward (see nextFollowTarget above).
  // `followNote` remembers the (from, to) pair across the swap — it lives
  // in this same component instance because onOpen just changes the
  // `cloudRunId` prop in place (LiveRunsPanel doesn't remount us), so the
  // note survives the transition and renders once `cloudRunId` becomes the
  // successor. It's cleared implicitly: a real unmount (Back to list, then
  // opening any other run) starts a fresh instance with no note at all.
  const [followNote, setFollowNote] = useState<{ from: string; to: string } | null>(null);
  const followTarget = nextFollowTarget(status, cloudRunId);
  useEffect(() => {
    if (!followTarget) return;
    setFollowNote({ from: cloudRunId, to: followTarget });
    onOpen(followTarget);
  }, [followTarget, cloudRunId, onOpen]);
  const justFollowed = followNote !== null && followNote.to === cloudRunId;

  // Rich live detail (stage rail, cost-so-far, event tail) from the SSE
  // stream. Prod-only (CF-cookie auth); degrades to the polled state in dev.
  const live = useOperatorRunEvents(server, cloudRunId);
  const stageStates = deriveStageStates(live.events, status?.chain_state);
  const conv = fanoutConverged(live.events);
  const auditConv = fanoutConverged(live.events, "audit_progress");
  const synth = synthesisStep(live.events);
  const patterns = derivePatternProgress(live.events);
  // "So far" moves during fanout (per-pattern costs before the stage rollup)
  // and reads journey-wide (the consumed preview's discovery cost).
  const previewCost = status?.journey_preview_cost_usd ?? 0;
  const liveCost =
    costSoFar(live.events) + patternCostInFlight(live.events) + previewCost;
  // 1s tick drives the per-chip elapsed clocks while any pattern is running.
  const anyRunning = patterns.some((pp) => pp.status === "running");
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (!anyRunning) return;
    const t = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(t);
  }, [anyRunning]);

  return (
    <div className="operator-panel">
      <div className="live-detail-head">
        <button type="button" className="live-back" onClick={onBack}>
          ← Live runs
        </button>
        <code className="live-detail-id">{cloudRunId}</code>
        {cancellable && (
          <button
            type="button"
            className="operator-row-action operator-row-action--danger"
            disabled={cancelling}
            onClick={onCancel}
          >
            {cancelling ? "Cancelling…" : "Cancel run"}
          </button>
        )}
      </div>

      {justFollowed && (
        <p className="live-follow-note" role="status">
          Discovery finished — following the full analysis run
        </p>
      )}

      {state.kind === "error" && (
        <div className="operator-error">Could not load: {state.message}</div>
      )}
      {state.kind === "loading" && !status && (
        <div className="operator-loading">Loading…</div>
      )}

      {status && (
        <div className="live-flat">
          {/* Journey signpost: a consumed preview's detail dead-ended the
              operator (live report, 2026-07-02) — point forward. */}
          {status.successor_run_id && (
            <div className="live-journey-banner" role="note">
              <span>
                This preview has been consumed — the analysis continued as a
                separate run.
              </span>
              <button
                type="button"
                className="operator-row-action"
                onClick={() => onOpen(status.successor_run_id!)}
              >
                Open the full run →
              </button>
            </div>
          )}

          {/* Status band — one flat strip, no boxes-in-boxes. */}
          <dl className="live-band">
            <div className="live-band-item">
              <dt>State</dt>
              <dd>
                <LiveBadge status={status.chain_state} />
              </dd>
            </div>
            <div className="live-band-item">
              <dt>Kind</dt>
              <dd>{status.chain_kind}</dd>
            </div>
            {status.chain_state === "queued" &&
              status.queue_position != null && (
                <div className="live-band-item">
                  <dt>Queue</dt>
                  <dd>{status.queue_position} ahead</dd>
                </div>
              )}
            <div className="live-band-item">
              <dt>Started</dt>
              <dd>{formatLocalTime(status.started_at)}</dd>
            </div>
            <div className="live-band-item">
              <dt>Elapsed</dt>
              <dd>{elapsed}</dd>
            </div>
            {status.exited_at && (
              <div className="live-band-item">
                <dt>
                  {status.exit_code == null
                    ? "Exited"
                    : status.exit_code !== 0
                      ? "Failed"
                      : status.chain_kind === "preview"
                        ? "Preview done"
                        : "Finished"}
                </dt>
                <dd>
                  {formatLocalTime(status.exited_at)}
                  {status.exit_code != null &&
                    status.exit_code !== 0 &&
                    ` · exit ${status.exit_code}`}
                </dd>
              </div>
            )}
            {(status.owner_name || status.owner_email || status.notify_email) && (
              <div className="live-band-item">
                <dt>Owner</dt>
                <dd>
                  <OwnerCell
                    name={status.owner_name}
                    email={status.owner_email ?? status.notify_email}
                  />
                </dd>
              </div>
            )}
            {status.client_id_masked && (
              <div className="live-band-item">
                <dt>Client</dt>
                <dd>
                  <code>{status.client_id_masked}</code>
                </dd>
              </div>
            )}
            {live.status === "live" && (
              <div className="live-band-item">
                <dt>So far</dt>
                <dd>
                  ${liveCost.toFixed(2)}
                  {previewCost > 0 && (
                    <span className="muted">
                      {" "}
                      (incl. preview ${previewCost.toFixed(2)})
                    </span>
                  )}
                </dd>
              </div>
            )}
            {est?.total != null && (
              <div className="live-band-item">
                <dt>Estimated</dt>
                <dd>
                  ${est.total.toFixed(2)}
                  {est.low != null && est.high != null && (
                    <span className="muted">
                      {" "}
                      (${est.low.toFixed(2)}–${est.high.toFixed(2)})
                    </span>
                  )}
                </dd>
              </div>
            )}
          </dl>

          {/* Stage rail + per-pattern strip — full width. */}
          <h3 className="live-h">Progress</h3>
          {live.status === "unavailable" ? (
            <p className="muted">
              Live stage stream connects behind Access — unavailable on this
              surface.
            </p>
          ) : (
            <>
              <div className="live-rail live-rail--row">
                {(status?.chain_kind === "preview"
                  ? PREVIEW_RAIL_STAGES
                  : RAIL_STAGES
                ).map((st) => (
                  <div
                    key={st}
                    className={`live-step live-step--${stageStates[st]}`}
                  >
                    <span className="live-step-tag">
                      {stageStates[st] === "done"
                        ? "done"
                        : stageStates[st] === "now"
                          ? "running"
                          : "todo"}
                    </span>
                    <span className="live-step-name">
                      {st}
                      {st === "fanout" && conv && (
                        <span className="live-step-count">
                          {" "}· {conv.done} of {conv.total}
                        </span>
                      )}
                      {st === "audit" && auditConv && (
                        <span className="live-step-count">
                          {" "}· {auditConv.done} of {auditConv.total}
                        </span>
                      )}
                      {st === "synthesis" && synth && (
                        <span className="live-step-count">
                          {" "}· step {synth.step}
                          {synth.status === "done" ? " ✓" : ""}
                        </span>
                      )}
                    </span>
                  </div>
                ))}
              </div>
              {patterns.length > 0 && (
                <div className="live-patterns live-patterns--wide">
                  {patterns.map((p) => {
                    const clockMs =
                      p.status === "running"
                        ? p.startedAtMs != null
                          ? nowMs - p.startedAtMs
                          : null
                        : p.startedAtMs != null && p.endedAtMs != null
                          ? p.endedAtMs - p.startedAtMs
                          : null;
                    const clock =
                      clockMs != null && clockMs >= 0
                        ? formatClock(clockMs)
                        : null;
                    return (
                      <span
                        key={p.id}
                        className={`live-pattern live-pattern--${p.status}`}
                        title={`${p.id} — ${p.status}`}
                      >
                        {p.id}
                        <span className="live-pattern-meta">
                          {p.status === "running" && (
                            <>
                              {typeof p.toolCalls === "number" &&
                                ` · ${p.toolCalls} calls`}
                              {clock && ` · ${clock}`}
                            </>
                          )}
                          {p.status === "done" && (
                            <>
                              {typeof p.quotes === "number" && ` · ${p.quotes}q`}
                              {typeof p.costUsd === "number" &&
                                ` · $${p.costUsd.toFixed(2)}`}
                              {clock && ` · ${clock}`}
                            </>
                          )}
                          {p.status === "error" && " · err"}
                        </span>
                      </span>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {/* Event tail — capped + scrollable, no more silent truncation. */}
          <h3 className="live-h">Events</h3>
          {live.events.length > 0 ? (
            <ul className="live-events live-events--scroll">
              {live.events.map((ev, i) => (
                <li key={i}>
                  <code>{ev.type}</code>
                  <span>{formatLiveEvent(ev)}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted">
              {live.status === "unavailable"
                ? "Event stream connects behind Access."
                : live.status === "connecting"
                  ? "Connecting…"
                  : "No events yet."}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
